import React from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";
import { classNames } from "primereact/utils";
import {
    Form,
    Link,
    redirect,
    useLoaderData,
    useNavigation,
    useSearchParams,
    useSubmit
} from "react-router-dom";
import { useFormik } from "formik";
import { getUserSettings, testClientEmailTemplate, testContractTemplate, testUpcomingRentalNotification, updateUserSettings } from "../data/accounts";
import { getWarehouses } from "../data/loca/warehouses";
import { getServices } from "../data/loca/services";
import { getDeliveryFuelPrice } from "../data/loca/rentals";
import { notify, send } from "../services/NotificationService";
import "../assets/css/delivery-vehicle.css";

const fuelTypeOptions = [
    { label: "Gazole", value: "gazole" },
    { label: "SP95", value: "sp95" },
    { label: "SP95-E10", value: "e10" },
    { label: "SP98", value: "sp98" },
    { label: "E85", value: "e85" },
    { label: "GPL", value: "gplc" }
];

const depositOptions = [
    { label: "Carte bancaire", value: "CB" },
    { label: "Espèces", value: "Cash" },
    { label: "Chèque", value: "Check" },
    { label: "Virement instantané", value: "BankTransfer" }
];
const NO_DEFAULT_DEPOSIT_TYPE = "__NO_DEFAULT__";
const settingsCategories = [
    { id: "calendar", label: "Calendrier", icon: "pi-calendar", description: "Disponibilités et délais" },
    { id: "delivery", label: "Livraison", icon: "pi-truck", description: "Tarif des trajets" },
    { id: "contract", label: "Contrat", icon: "pi-file-pdf", description: "Modèle et clauses" },
    { id: "stock", label: "Stock", icon: "pi-box", description: "Marchandises" },
    { id: "deposit", label: "Cautions", icon: "pi-shield", description: "Moyens et relances" },
    { id: "notifications", label: "Alertes", icon: "pi-bell", description: "Notifications" },
    { id: "payment", label: "Encaissement", icon: "pi-credit-card", description: "Acompte et frais" },
    { id: "emails", label: "Emails", icon: "pi-envelope", description: "Messages client" }
];
// Tarif de livraison : saisi en euros dans le formulaire, stocke en centimes par l'API.
// Les deux propositions (au temps, à la distance) sont toujours calculées ; ce réglage dit laquelle est cochée par défaut.
const deliveryPricingModeOptions = [
    { label: "La plus avantageuse pour vous (recommandé)", value: "Best" },
    { label: "Toujours au temps de trajet", value: "PerMinute" },
    { label: "Toujours à la distance", value: "PerKilometer" }
];
const centsToEuros = (cents, fallbackEuros) => (cents === null || cents === undefined ? fallbackEuros : Number(cents) / 100);
const eurosToCents = (euros) => Math.max(0, Math.round(Number(euros || 0) * 100));
const roundUpToStep = (cents, stepCents) => (stepCents > 0 ? Math.ceil(cents / stepCents) * stepCents : cents);
// Meme regle que l'API : max(minimum, arrondi_sup(forfait + unites x tarif)).
const computeDeliveryLegExampleCents = (values, mode, units) => {
    const rateCents = eurosToCents(mode === "PerKilometer" ? values.deliveryPricePerKilometer : values.deliveryPricePerMinute);
    const rawCents = Math.ceil(eurosToCents(values.deliveryPricingBase) + units * rateCents);
    return Math.max(roundUpToStep(rawCents, eurosToCents(values.deliveryPricingRounding)), eurosToCents(values.deliveryPricingMinimum));
};
// Vrai quand le calcul brut est sous le minimum parametre (le formulaire le signale alors dans la proposition).
const isDeliveryMinimumApplied = (values, mode, units) => {
    const rateCents = eurosToCents(mode === "PerKilometer" ? values.deliveryPricePerKilometer : values.deliveryPricePerMinute);
    const rawCents = Math.ceil(eurosToCents(values.deliveryPricingBase) + units * rateCents);
    return roundUpToStep(rawCents, eurosToCents(values.deliveryPricingRounding)) < eurosToCents(values.deliveryPricingMinimum);
};
// Meme regle que l'API : mode impose, sinon la proposition la plus elevee (egalite => au temps).
const chooseRecommendedDeliveryMode = (mode, perMinuteCents, perKilometerCents) => {
    if (mode === "PerMinute" || mode === "PerKilometer") {
        return mode;
    }
    return perKilometerCents > perMinuteCents ? "PerKilometer" : "PerMinute";
};
const formatEuros = (cents) => `${(cents / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const pushoverSoundOptions = [
    { label: "Pushover (default)", value: "pushover" }, { label: "Bike", value: "bike" }, { label: "Bugle", value: "bugle" }, { label: "Cash Register", value: "cashregister" }, { label: "Classical", value: "classical" }, { label: "Cosmic", value: "cosmic" }, { label: "Falling", value: "falling" }, { label: "Gamelan", value: "gamelan" }, { label: "Incoming", value: "incoming" }, { label: "Intermission", value: "intermission" }, { label: "Magic", value: "magic" }, { label: "Mechanical", value: "mechanical" }, { label: "Piano Bar", value: "pianobar" }, { label: "Siren", value: "siren" }, { label: "Space Alarm", value: "spacealarm" }, { label: "Tug Boat", value: "tugboat" }, { label: "Alien Alarm (long)", value: "alien" }, { label: "Climb (long)", value: "climb" }, { label: "Persistent (long)", value: "persistent" }, { label: "Pushover Echo (long)", value: "echo" }, { label: "Up Down (long)", value: "updown" }, { label: "Vibrate Only", value: "vibrate" }, { label: "None (silent)", value: "none" }
];
const clientEmailTemplateLabels = {
    contract_info: "Envoi du contrat",
    signed_contract: "Contrat signé",
    deposit_link: "Lien de caution",
    deposit_regenerated_link: "Nouveau lien de caution",
    deposit_released: "Caution libérée",
    deposit_partially_released: "Caution partiellement libérée",
    deposit_reminder: "Relance caution avant location",
    advance_payment_link: "Lien d'acompte",
    advance_payment_reminder: "Relance acompte",
    advance_payment_final_reminder: "Dernière relance acompte",
    advance_payment_unpaid_closure: "Location clôturée faute d'acompte",
    advance_payment_received: "Acompte reçu",
    electronic_signature_reminder: "Relance signature électronique",
    electronic_signature_final_reminder: "Dernière relance signature électronique",
    payment_authorized: "Demande de paiement",
    payment_received: "Paiement reçu",
    end_rental: "Fin de location",
    satisfaction_survey: "Questionnaire de satisfaction",
    satisfaction_reminder: "Rappel questionnaire de satisfaction",
    client_cancellation: "Annulation prise en compte",
    lessor_cancellation: "Location annulée par le loueur"
};
const emailTemplateVariables = [
    { token: "{{clientFirstName}}", description: "Prénom du client. Exemple : Julie." },
    { token: "{{clientLastName}}", description: "Nom du client. Exemple : Martin." },
    { token: "{{rentalName}}", description: "Nom du matériel ou de la location. Exemple : Machine à glace à l'italienne." },
    { token: "{{rentalDates}}", description: "Dates de la location. Exemple : du 25/10/2026 au 27/10/2026, ou le 25/10/2026 sur une journée." },
    { token: "{{rentalPublicLink}}", description: "Lien public de suivi de la location côté client. Exemple : https://.../location/abc123." },
    { token: "{{depositLink}}", description: "Lien direct pour déposer la caution. Exemple : https://.../deposit/abc123." },
    { token: "{{advancePaymentLink}}", description: "Lien direct pour déposer l'acompte. Exemple : https://.../advance-payment/abc123." },
    { token: "{{advanceAmount}}", description: "Montant de l'acompte attendu. Exemple : 30,00 €." },
    { token: "{{receivedAdvanceAmount}}", description: "Montant de l'acompte reçu lors d'une validation manuelle. Exemple : 30,00 €." },
    { token: "{{advancePaymentInstructions}}", description: "Instructions de paiement de l'acompte : lien de paiement ou virement instantané selon la date de location." },
    { token: "{{transferReference}}", description: "Motif à indiquer sur le virement. Exemple : Acompte abc123." },
    { token: "{{bankAccountDetails}}", description: "Coordonnées bancaires configurées pour les virements d'acompte." },
    { token: "{{signatureName}}", description: "Signature éditable dans les textes conditionnels. Exemple : prénom, nom de société ou formule personnalisée." },
    { token: "{{defaultSignatureName}}", description: "Valeur automatique de signature issue du profil. Exemple : prénom du loueur ou nom de société." },
    { token: "{{footer}}", description: "Pied de mail éditable dans les textes conditionnels. Exemple : lien vers le catalogue public et formule de contact." },
    { token: "{{catalogFooter}}", description: "Bloc de fin qui invite à consulter le catalogue. Exemple : Retrouvez tous nos produits ici : https://..." },
    { token: "{{timeMessageHeader}}", description: "Formule de salutation automatique selon l'heure. Exemple : Bonjour ou Bonsoir." },
    { token: "{{timeMessageFooter}}", description: "Formule de fin adaptée au moment de la journée. Exemple : Bonne journée ou Bonne soirée." },
    { token: "{{publicCatalogLink}}", description: "Lien public vers le catalogue du loueur. Exemple : https://.../catalogue/mon-compte." },
    { token: "{{satisfactionMessage}}", description: "Invitation à donner son avis (texte conditionnel éditable + lien du questionnaire). Vide si le questionnaire de satisfaction est désactivé. Si le modèle ne contient pas ce jeton, l'invitation est insérée automatiquement avant la formule de fin." },
    { token: "{{satisfactionLink}}", description: "Lien direct vers le questionnaire de satisfaction du locataire. Exemple : https://.../avis/abc123." },
    { token: "{{additionalInfos}}", description: "Consignes de caution ajoutées automatiquement à l'email d'envoi du contrat. Exemples : lien de caution déjà disponible, nouveau lien de caution généré, ou message indiquant que le client recevra un email 24h avant la location." },
    { token: "{{contractExtraInfo}}", description: "Informations automatiques propres au contrat. Exemples : contrat importé depuis Bricolib ou paiement en espèces à prévoir." },
    { token: "{{contractSignatureMessage}}", description: "Phrase adaptée au mode de signature. Exemples : signature électronique à réaliser, acompte à régler avant signature, ou signature sur place." },
    { token: "{{contractLocationMessage}}", description: "Phrase indiquant où retrouver l'adresse utile. Exemples : adresse de livraison visible dans le contrat, ou adresse du rendez-vous indiquée dans la location." },
    { token: "{{confirmReceiptMessage}}", description: "Phrase demandant au client de confirmer qu'il a bien reçu le contrat. Exemple : Merci de me confirmer la bonne réception de ce contrat." },
    { token: "{{paymentIntent}}", description: "Référence Stripe liée à une caution ou un paiement. Exemple : pi_3Pabc..." },
    { token: "{{capturedAmount}}", description: "Montant récupéré lors d'une libération partielle de caution. Exemple : 45,00 €." },
    { token: "{{capturedFees}}", description: "Liste des frais récupérés lors d'une libération partielle de caution." },
    { token: "{{cancellationReason}}", description: "Raison saisie lors de la suppression d'une location, quand vous choisissez de prévenir le locataire. Si le modèle ne contient pas ce jeton, la raison est insérée automatiquement avant la formule de fin." }
];
const contractTemplateVariableGroups = [
    { label: "Identités et signatures", variables: [
        { name: "rentalPublicId", description: "Identifiant public unique de la location." },
        { name: "lessorRole", description: "Le loueur pour un professionnel, sinon le propriétaire." },
        { name: "lessorNoun", description: "Le loueur ou le propriétaire, selon le compte." },
        { name: "lessorNounLower", description: "Le loueur ou le propriétaire, en minuscule." },
        { name: "lessorIdentity", description: "Identité complète du loueur issue du profil." },
        { name: "lessorSignatory", description: "Nom ou raison sociale du loueur au niveau de la signature." },
        { name: "tenantIdentity", description: "Identité complète du locataire issue de sa fiche client." },
        { name: "tenantSignatory", description: "Nom ou raison sociale du locataire au niveau de sa signature." },
        { name: "contractDate", description: "Date de début de la location." },
        { name: "signatureAgreementLabel", description: "Libellé de signature adapté selon la présence d’un acompte." }
    ] },
    { label: "Location et matériel", variables: [
        { name: "materials", description: "Matériels loués, accessoires et options classiques qui constituent la location, regroupés avec quantité." },
        { name: "options", description: "Liste uniquement les options liées à une marchandise/consommable. Les options classiques sont intégrées à {{materials}}." },
        { name: "rentalStart", description: "Date et heure de début." },
        { name: "rentalEnd", description: "Date et heure de fin." },
        { name: "rentalDuration", description: "Durée calculée automatiquement." },
        { name: "totalAmount", description: "Montant total de la location." },
        { name: "paidOptions", description: "Conditions ou options payantes propres aux matériels." }
    ] },
    { label: "Article 2 — livraison", variables: [
        { name: "deliveryTerms", description: "Toujours affiché dans l'article 2 : texte « livraison / reprise » ou « sans livraison / retard » selon la location." }
    ] },
    { label: "Articles 3 et 4 — conditions", variables: [
        { name: "advancePaymentTerms", description: "Seulement si un acompte est demandé ; sinon cette variable est vide." },
        { name: "serviceTerms", description: "Article 3bis réglé sur la plateforme de la location (fiche de la plateforme) ; vide si elle n'en a pas." },
        { name: "materialUseTerms", description: "Seulement si un matériel comporte des consignes d’utilisation." }
    ] },
    { label: "Article 6 — restitution", variables: [
        { name: "returnCollectionSentence", description: "Phrase de reprise éditable ; vide si le client restitue lui-même." },
        { name: "nonReturnConsequence", description: "Conséquence en cas de non-restitution, de retard, de perte ou de dégradation : retenue sur caution si elle existe, sinon facturation des frais au locataire." },
        { name: "materialReturnClauses", description: "Clauses de retour renseignées sur les matériels concernés." }
    ] },
    { label: "Article 8 — caution", variables: [
        { name: "depositTerms", description: "Toujours affiché : texte « avec caution » ou « sans caution » selon la location." }
    ] },
    { label: "Pied de page", variables: [
        { name: "catalogFooter", description: "Lien vers le catalogue public du loueur, s’il est activé." }
    ] }
];
const contractTemplateVariables = contractTemplateVariableGroups.flatMap((group) => group.variables);
const contractWordingConditionalKeys = new Set([
    "contract_delivery_terms", "contract_late_return_terms", "contract_return_collection_terms", "contract_return_consequence_with_deposit", "contract_return_consequence_without_deposit", "contract_deposit_terms", "contract_no_deposit_terms",
    "contract_advance_terms", "contract_rental_options_terms", "contract_options_terms", "contract_use_terms", "contract_signature_agreement_with_advance", "contract_signature_agreement_without_advance", "contract_catalog_footer_individual", "contract_catalog_footer_professional"
]);
const contractConditionalTextKeys = new Set([...contractWordingConditionalKeys, "contract_return_terms"]);
const contractWordingConditions = {
    contract_delivery_terms: "Article 2 — alimente {{deliveryTerms}} lorsqu'une livraison, une reprise ou une mise en place est prévue. Il est remplacé par « sans livraison / retard » dans les autres cas.",
    contract_late_return_terms: "Article 2 — alimente {{deliveryTerms}} lorsqu'aucune livraison ou reprise n'est prévue. Il remplace alors le texte « livraison / reprise ».",
    contract_return_collection_terms: "Article 6 — configure {{returnCollectionSentence}} ; elle s'affiche uniquement lorsque le loueur vient récupérer le matériel.",
    contract_return_consequence_with_deposit: "Article 6 — alimente {{nonReturnConsequence}} lorsqu'une caution est demandée. Le texte est placé après « le loueur pourra ».",
    contract_return_consequence_without_deposit: "Article 6 — alimente {{nonReturnConsequence}} lorsqu'aucune caution n'est demandée. Le texte est placé après « le loueur pourra ».",
    contract_signature_agreement_with_advance: "Fin du contrat — alimente {{signatureAgreementLabel}} lorsqu'un acompte est demandé.",
    contract_signature_agreement_without_advance: "Fin du contrat — alimente {{signatureAgreementLabel}} lorsqu'aucun acompte n'est demandé.",
    contract_catalog_footer_individual: "Pied de page — alimente {{catalogFooter}} pour un compte particulier si le catalogue public est activé.",
    contract_catalog_footer_professional: "Pied de page — alimente {{catalogFooter}} pour un compte professionnel si le catalogue public est activé.",
    contract_deposit_terms: "Article 8 — alimente {{depositTerms}} pour une caution CB, espèces ou chèque. Il est remplacé par la carte « sans caution » lorsqu'aucune caution n'est demandée.",
    contract_no_deposit_terms: "Article 8 — alimente {{depositTerms}} lorsqu'aucune caution n'est demandée. Il remplace alors le texte « avec caution ».",
    contract_advance_terms: "Article 3 — alimente {{advancePaymentTerms}} uniquement lorsqu'un acompte supérieur à zéro est demandé ; sinon la variable est vide.",
    contract_rental_options_terms: "Article 1 — ajoute les options classiques dans {{materials}}, car elles constituent la location. Ce texte correspond au champ libre « Autres options » et aux options non liées à une marchandise.",
    contract_options_terms: "Article 1 — alimente {{options}} uniquement lorsqu'au moins une option liée à une marchandise est sélectionnée. Les options classiques restent dans {{materials}} car elles constituent la location.",
    contract_use_terms: "Article 4 — alimente {{materialUseTerms}} uniquement lorsqu'un matériel comporte des consignes d'utilisation."
};
const contractWordingVariables = {
    contract_delivery_terms: [
        { token: "{{lessorNoun}}", description: "Le loueur ou le propriétaire, selon le compte." },
        { token: "{{deliveryService}}", description: "La livraison, la reprise et/ou la mise en service prévues." },
        { token: "{{deliveryAddress}}", description: "Adresse de livraison renseignée sur la location, sinon adresse du client." }
    ],
    contract_late_return_terms: [
        { token: "{{hourlyPenaltyAmount}}", description: "Total de la pénalité horaire des matériels loués." }
    ],
    contract_return_collection_terms: [
        { token: "{{lessorNoun}}", description: "Le loueur ou le propriétaire, selon le compte." },
        { token: "{{returnCollectionAddress}}", description: "Adresse du client à laquelle le matériel est repris." }
    ],
    contract_catalog_footer_individual: [
        { token: "{{publicCatalogLink}}", description: "Lien public vers le catalogue du loueur." }
    ],
    contract_catalog_footer_professional: [
        { token: "{{publicCatalogLink}}", description: "Lien public vers le catalogue du loueur." }
    ],
    contract_deposit_terms: [
        { token: "{{lessorDative}}", description: "Au loueur ou au propriétaire, selon le compte." },
        { token: "{{depositAmount}}", description: "Montant total de la caution des matériels loués." },
        { token: "{{depositMethod}}", description: "Empreinte CB, espèces ou chèque selon la location." }
    ],
    contract_no_deposit_terms: [
        { token: "{{lessorNounLower}}", description: "Le loueur ou le propriétaire, en minuscule." }
    ],
    contract_signature_electronic_with_advance: [
        { token: "{{advancePaymentPageAction}}", description: "Phrase adaptée au mode d'acompte : dépôt CB sur la page, informations bancaires sur la page, ou règlement hors plateforme convenu." }
    ],
    contract_advance_terms: [
        { token: "{{lessorDative}}", description: "Au loueur ou au propriétaire, selon le compte." },
        { token: "{{lessorNounLower}}", description: "Le loueur ou le propriétaire, en minuscule." },
        { token: "{{advanceAmount}}", description: "Montant d'acompte demandé pour la location." }
    ],
    contract_rental_options_terms: [
        { token: "{{classicOptionsList}}", description: "Liste des options classiques : champ libre « Autres options » et options non liées à une marchandise, regroupées avec leurs quantités." }
    ],
    contract_options_terms: [
        { token: "{{optionsIntro}}", description: "Phrase d'introduction renseignée uniquement si au moins une option est liée à une marchandise." },
        { token: "{{optionsList}}", description: "Liste uniquement les options liées à une marchandise/consommable, avec leurs quantités." }
    ],
    contract_use_terms: [
        { token: "{{useInstructions}}", description: "Texte des consignes définies sur les matériels loués. Le titre ou l'introduction est entièrement modifiable dans cette carte." }
    ]
};
const contractTestScenarioGroups = [
    { id: "delivery", label: "Livraison", options: [{ label: "Livraison avec reprise", value: "contract_delivery_terms" }, { label: "Sans livraison / pénalité de retard", value: "contract_late_return_terms" }] },
    { id: "deposit", label: "Caution", options: [{ label: "Caution CB", value: "contract_deposit_terms" }, { label: "Sans caution", value: "contract_no_deposit_terms" }] },
    { id: "advance", label: "Acompte", options: [{ label: "Avec acompte", value: "contract_advance_terms" }, { label: "Sans acompte", value: "contract_no_advance_terms" }] },
    { id: "rentalOptions", label: "Options classiques", options: [{ label: "Avec options de location", value: "contract_rental_options_terms" }, { label: "Sans option classique", value: "contract_no_rental_options_terms" }] },
    { id: "options", label: "Marchandises", options: [{ label: "Avec marchandises / consommables", value: "contract_options_terms" }, { label: "Sans marchandise", value: "contract_no_options_terms" }] },
    { id: "use", label: "Consignes", options: [{ label: "Avec consignes", value: "contract_use_terms" }, { label: "Sans consigne", value: "contract_no_use_terms" }] },
    // Complété au rendu par les plateformes qui ont un article 3bis (contract_service_<id>).
    { id: "platform", label: "Plateforme", options: [{ label: "Aucune", value: "contract_no_platform" }] }
];

const conditionalEmailTextGroups = [
    {
        id: "timeMessageHeader",
        title: "{{timeMessageHeader}}",
        label: "Salutation",
        description: "Formule de début de mail selon l'heure d'envoi.",
        keys: ["time_header_day", "time_header_evening"]
    },
    {
        id: "timeMessageFooter",
        title: "{{timeMessageFooter}}",
        label: "Formule de fin",
        description: "Phrase de fin de mail selon l'heure d'envoi.",
        keys: ["time_footer_day", "time_footer_evening"]
    },
    {
        id: "additionalInfos",
        title: "{{additionalInfos}}",
        label: "Consignes de caution",
        description: "Textes ajoutés à l'envoi du contrat selon la caution à déposer.",
        keys: ["additional_deposit_permanent_link", "additional_deposit_immediate_link", "additional_deposit_future_reminder", "additional_deposit_bank_transfer_instruction"]
    },
    {
        id: "advancePaymentInstructions",
        title: "{{advancePaymentInstructions}}",
        label: "Instructions d'acompte",
        description: "Textes utilises dans les mails selon le mode d'acompte.",
        keys: [
            "advance_payment_page_action_bank_transfer",
            "advance_payment_page_action_manual",
            "advance_payment_page_action_online",
            "advance_payment_instructions_bank_transfer_page",
            "advance_payment_instructions_manual",
            "advance_payment_instructions_online"
        ]
    },
    {
        id: "contractExtraInfo",
        title: "{{contractExtraInfo}}",
        label: "Informations contrat",
        description: "Textes ajoutés au contrat selon son origine ou le moyen de paiement.",
        keys: ["contract_extra_bricolib", "contract_extra_cash_no_advance", "contract_extra_card_payment_notice"]
    },
    {
        id: "contractSignatureMessage",
        title: "{{contractSignatureMessage}}",
        label: "Signature du contrat",
        description: "Message adapté au mode de signature du contrat.",
        keys: [
            "contract_signature_electronic_with_advance",
            "contract_signature_electronic_without_advance",
            "contract_signature_in_person_delivery",
            "contract_signature_in_person_no_delivery"
        ]
    },
    {
        id: "contractLocationMessage",
        title: "{{contractLocationMessage}}",
        label: "Lieu de la location",
        description: "Phrase ajoutée après signature selon le type de remise du matériel.",
        keys: ["contract_location_delivery", "contract_location_appointment"]
    },
    {
        id: "confirmReceiptMessage",
        title: "{{confirmReceiptMessage}}",
        label: "Confirmation de réception",
        description: "Demande de confirmation lorsque cette confirmation est prévue pour l'envoi.",
        keys: ["confirm_receipt"]
    },
    {
        id: "satisfactionMessage",
        title: "{{satisfactionMessage}}",
        label: "Invitation à donner son avis",
        description: "Invitation au questionnaire de satisfaction, ajoutée aux mails de fin de location selon le type de compte loueur.",
        keys: ["satisfaction_invitation_individual", "satisfaction_invitation_professional"]
    },
    {
        id: "signatureName",
        title: "{{signatureName}}",
        label: "Signature",
        description: "Nom ou formule de signature selon le type de compte loueur.",
        keys: ["signature_individual", "signature_professional"]
    },
    {
        id: "footer",
        title: "{{footer}}",
        label: "Pied de mail",
        description: "Bloc final du mail selon le type de compte loueur.",
        keys: ["footer_individual", "footer_professional"]
    }
];
const conditionalEmailTextConditionLabels = {
    time_header_day: "Avant 19h",
    time_header_evening: "À partir de 19h",
    time_footer_day: "Avant 19h",
    time_footer_evening: "À partir de 19h",
    additional_deposit_permanent_link: "Lien de caution permanent",
    additional_deposit_immediate_link: "Lien envoyé avec le contrat",
    additional_deposit_future_reminder: "Lien envoyé plus tard",
    additional_deposit_bank_transfer_instruction: "Caution par virement instantané",
    advance_payment_page_action_bank_transfer: "Action page - virement",
    advance_payment_page_action_manual: "Action page - hors plateforme",
    advance_payment_page_action_online: "Action page - CB",
    advance_payment_instructions_bank_transfer_page: "Instructions mail - infos bancaires sur page",
    advance_payment_instructions_manual: "Instructions mail - hors plateforme",
    advance_payment_instructions_online: "Instructions mail - CB",
    contract_extra_bricolib: "Location issue de Bricolib",
    contract_extra_cash_no_advance: "Paiement espèces sans acompte",
    contract_extra_card_payment_notice: "Paiement CB sur place",
    contract_signature_electronic_with_advance: "Signature électronique avec acompte",
    contract_signature_electronic_without_advance: "Signature électronique sans acompte",
    contract_signature_in_person_delivery: "Signature sur place avec livraison",
    contract_signature_in_person_no_delivery: "Signature sur place sans livraison",
    contract_location_delivery: "Location livrée",
    contract_location_appointment: "Retrait ou rendez-vous",
    confirm_receipt: "Confirmation prévue pour l'envoi",
    satisfaction_invitation_individual: "Compte particulier",
    satisfaction_invitation_professional: "Compte professionnel",
    signature_individual: "Compte particulier",
    signature_professional: "Compte professionnel",
    footer_individual: "Compte particulier",
    footer_professional: "Compte professionnel"
};
const normalizeAdvancePaymentBankTransferReferenceTemplate = (value) => {
    const trimmedValue = (value ?? "").trim();
    return !trimmedValue || trimmedValue.toLowerCase() === "location {{rentalpublicid}}"
        ? "Acompte {{rentalPublicId}}"
        : trimmedValue;
};

export async function loader() {
    const settings = await getUserSettings();
    // Entrepots pour le point de depart par defaut des estimations de livraison (facultatif : la page reste utilisable sans).
    let warehouses = [];
    try {
        const response = await getWarehouses();
        warehouses = Array.isArray(response) ? response : [];
    } catch (error) {
        warehouses = [];
    }
    // Plateformes pour l'aperçu du contrat (article 3bis) : facultatif aussi.
    let services = [];
    try {
        const response = await getServices();
        services = Array.isArray(response) ? response : [];
    } catch (error) {
        services = [];
    }
    return { settings, warehouses, services };
}

export async function action({ request }) {
    const formData = await request.formData();
    const data = Object.fromEntries(formData);
    const allowedDepositTypes = formData.getAll("allowedDepositTypes");
    const clientEmailTemplates = JSON.parse(data.clientEmailTemplatesJson || "[]");
    const clientEmailConditionalTexts = JSON.parse(data.clientEmailConditionalTextsJson || "[]");
    const paymentFeeConfigurations = JSON.parse(data.paymentFeeConfigurationsJson || "[]");

    try {
        await updateUserSettings({
            RentalBufferBeforeMinutes: Number(data.rentalBufferBeforeMinutes || 0),
            RentalBufferAfterMinutes: Number(data.rentalBufferAfterMinutes || 0),
            RentalCalendarEventDurationMinutes: Number(data.rentalCalendarEventDurationMinutes || 30),
            RentalDeliveryCalendarEventDurationMinutes: Number(data.rentalDeliveryCalendarEventDurationMinutes || 60),
            RentalDeliveryWithSetupCalendarEventDurationMinutes: Number(data.rentalDeliveryWithSetupCalendarEventDurationMinutes || 120),
            RentalPickupCalendarEventDurationMinutes: Number(data.rentalPickupCalendarEventDurationMinutes || 60),
            GoogleCalendarSyncEnabled: data.googleCalendarSyncEnabled === "true",
            GoogleCalendarId: data.googleCalendarId || "",
            GoogleCalendarTimeZone: data.googleCalendarTimeZone || "Europe/Paris",
            GoogleCalendarServiceAccountJson: data.googleCalendarServiceAccountJson || "",
            AllowedDepositTypes: allowedDepositTypes,
            DefaultDepositType: data.defaultDepositType === NO_DEFAULT_DEPOSIT_TYPE ? "" : data.defaultDepositType,
            RequireElectronicSignatureByDefault: data.requireElectronicSignatureByDefault === "true",
            DefaultAdvancePaymentPercent: Number(data.defaultAdvancePaymentPercent || 0),
            AdvancePaymentReminderDelayDays: Number(data.advancePaymentReminderDelayDays || 0),
            AutoSendDepositReminder: data.autoSendDepositReminder === "true",
            DepositReminderDelayHours: Number(data.depositReminderDelayHours || 2),
            EnablePublicAdvancePayment: data.enablePublicAdvancePayment === "true",
            EnablePublicRentalPayment: data.enablePublicRentalPayment === "true",
            EnableSatisfactionSurvey: data.enableSatisfactionSurvey === "true",
            DeliveryPricingMode: data.deliveryPricingMode || "Best",
            DeliveryPricePerMinuteCents: Number(data.deliveryPricePerMinuteCents || 0),
            DeliveryPricePerKilometerCents: Number(data.deliveryPricePerKilometerCents || 0),
            DeliveryPricingBaseCents: Number(data.deliveryPricingBaseCents || 0),
            DeliveryPricingRoundingCents: Number(data.deliveryPricingRoundingCents || 0),
            DeliveryPricingMinimumCents: Number(data.deliveryPricingMinimumCents || 0),
            DeliverySetupSupplementCents: Number(data.deliverySetupSupplementCents || 0),
            UseTravelTimeForDeliveryCalendarEvents: data.useTravelTimeForDeliveryCalendarEvents === "true",
            DeliveryDefaultOriginKey: data.deliveryDefaultOriginKey || "auto",
            FuelSurchargeEnabled: data.fuelSurchargeEnabled === "true",
            FuelType: data.fuelType || "gazole",
            FuelConsumptionLitersPer100Km: Number(data.fuelConsumptionLitersPer100Km || 0),
            FuelReferencePriceCents: Number(data.fuelReferencePriceCents || 0),
            PaymentFeeConfigurations: paymentFeeConfigurations,
            EnableAdvancePaymentBankTransferInstruction: data.enableAdvancePaymentBankTransferInstruction === "true",
            AdvancePaymentBankTransferThresholdDays: Number(data.advancePaymentBankTransferThresholdDays || 15),
            AdvancePaymentBankTransferBeneficiary: data.advancePaymentBankTransferBeneficiary || "",
            AdvancePaymentBankTransferIban: data.advancePaymentBankTransferIban || "",
            AdvancePaymentBankTransferBic: data.advancePaymentBankTransferBic || "",
            AdvancePaymentBankTransferBankName: data.advancePaymentBankTransferBankName || "",
            AdvancePaymentBankTransferReferenceTemplate: normalizeAdvancePaymentBankTransferReferenceTemplate(data.advancePaymentBankTransferReferenceTemplate),
            AdvancePaymentBankTransferInstructionTemplate: data.advancePaymentBankTransferInstructionTemplate || "",
            EnableUpcomingRentalPushNotification: data.enableUpcomingRentalPushNotification === "true",
            UpcomingRentalNotificationLeadTimeHours: Number(data.upcomingRentalNotificationLeadTimeHours || 6),
            PushoverUser: data.pushoverUser || "",
            PushoverToken: data.pushoverToken || "",
            PushoverSound: data.pushoverSound || "pushover",
            MerchandisePurchaseReminderHorizonDays: Number(data.merchandisePurchaseReminderHorizonDays || 14),
            DefaultMerchandiseUnit: data.defaultMerchandiseUnit || "pièce",
            DefaultMerchandiseReorderThreshold: Number(data.defaultMerchandiseReorderThreshold || 0),
            AutoDeductMerchandiseStockOnRental: data.autoDeductMerchandiseStockOnRental === "true",
            AutoRestoreMerchandiseStockOnCancellation: data.autoRestoreMerchandiseStockOnCancellation === "true",
            ContractTemplateBody: data.contractTemplateBody || "",
            ContractTemplateHeader: data.contractTemplateHeader || "",
            ContractTemplateFooter: data.contractTemplateFooter || "",
            ClientEmailTemplates: clientEmailTemplates,
            ClientEmailConditionalTexts: clientEmailConditionalTexts
        });
    } catch (error) {
        send("warning", error.message || "Erreur lors de l'enregistrement des paramètres.");
        notify();
        return null;
    }

    return redirect("/parametres?saved=1");
}

export default function SettingsPage() {
    const { settings, warehouses, services } = useLoaderData();
    const contractTestScenarioGroupsWithPlatforms = React.useMemo(() => contractTestScenarioGroups.map((group) => group.id !== "platform"
        ? group
        : {
            ...group,
            options: [
                ...group.options,
                ...(services || [])
                    .filter((service) => (service.contractTerms || "").trim())
                    .map((service) => ({ label: service.name, value: `contract_service_${service.id}` }))
            ]
        }), [services]);
    const deliveryOriginOptions = [
        { label: "Automatique : entrepôt du matériel, sinon mon adresse", value: "auto" },
        { label: "Mon adresse (celle du compte)", value: "profile" },
        ...(Array.isArray(warehouses) ? warehouses : []).map((warehouse) => ({
            label: [warehouse.name, [warehouse.address, warehouse.zipcode, warehouse.city].filter(Boolean).join(" ")].filter(Boolean).join(" · "),
            value: `warehouse:${warehouse.id}`
        }))
    ];
    const [searchParams] = useSearchParams();
    const navigation = useNavigation();
    const submit = useSubmit();
    const isSaving = navigation.state === "submitting";
    const clientEmailTemplates = (settings?.clientEmailTemplates ?? []).map((template) => ({
        ...template,
        label: clientEmailTemplateLabels[template.key] ?? template.label
    }));
    const variableFieldRefs = React.useRef({});
    const [variableAutocomplete, setVariableAutocomplete] = React.useState(null);
    const [emailVariableSearch, setEmailVariableSearch] = React.useState("");
    const [conditionalTextSearch, setConditionalTextSearch] = React.useState("");
    const [emailTemplateSearch, setEmailTemplateSearch] = React.useState("");
    const [testingEmailTemplateKey, setTestingEmailTemplateKey] = React.useState(null);
    const [testingContractScenario, setTestingContractScenario] = React.useState(null);
    const [expandedContractWordingCards, setExpandedContractWordingCards] = React.useState({});
    const [selectedContractTestScenarios, setSelectedContractTestScenarios] = React.useState(() =>
        Object.fromEntries(contractTestScenarioGroups.map((group) => [group.id, group.options[0].value]))
    );
    const [testingUpcomingRentalNotification, setTestingUpcomingRentalNotification] = React.useState(false);
    const [conditionalTestTemplateKey, setConditionalTestTemplateKey] = React.useState("contract_info");
    const [selectedConditionalTextKeys, setSelectedConditionalTextKeys] = React.useState(() =>
        Object.fromEntries(conditionalEmailTextGroups.map((group) => [group.id, group.keys[0]]))
    );
    const [activeSettingsCategory, setActiveSettingsCategory] = React.useState("calendar");
    const [googleCalendarHelpVisible, setGoogleCalendarHelpVisible] = React.useState(false);

    const formik = useFormik({
        initialValues: {
            rentalBufferBeforeMinutes: settings?.rentalBufferBeforeMinutes ?? 60,
            rentalBufferAfterMinutes: settings?.rentalBufferAfterMinutes ?? 60,
            rentalCalendarEventDurationMinutes: settings?.rentalCalendarEventDurationMinutes ?? 30,
            rentalDeliveryCalendarEventDurationMinutes: settings?.rentalDeliveryCalendarEventDurationMinutes ?? 60,
            rentalDeliveryWithSetupCalendarEventDurationMinutes: settings?.rentalDeliveryWithSetupCalendarEventDurationMinutes ?? 120,
            rentalPickupCalendarEventDurationMinutes: settings?.rentalPickupCalendarEventDurationMinutes ?? 60,
            googleCalendarSyncEnabled: settings?.googleCalendarSyncEnabled ?? false,
            googleCalendarId: settings?.googleCalendarId ?? "",
            googleCalendarTimeZone: settings?.googleCalendarTimeZone ?? "Europe/Paris",
            googleCalendarServiceAccountJson: settings?.googleCalendarServiceAccountJson ?? "",
            allowedDepositTypes: settings?.allowedDepositTypes ?? ["CB", "Cash", "Check", "BankTransfer"],
            defaultDepositType: settings?.defaultDepositType || NO_DEFAULT_DEPOSIT_TYPE,
            requireElectronicSignatureByDefault: settings?.requireElectronicSignatureByDefault ?? true,
            defaultAdvancePaymentPercent: settings?.defaultAdvancePaymentPercent ?? 0,
            advancePaymentReminderDelayDays: settings?.advancePaymentReminderDelayDays ?? 2,
            autoSendDepositReminder: settings?.autoSendDepositReminder ?? true,
            depositReminderDelayHours: settings?.depositReminderDelayHours ?? 2,
            enablePublicAdvancePayment: settings?.enablePublicAdvancePayment ?? true,
            enablePublicRentalPayment: settings?.enablePublicRentalPayment ?? true,
            enableSatisfactionSurvey: settings?.enableSatisfactionSurvey ?? true,
            deliveryPricingMode: settings?.deliveryPricingMode ?? "Best",
            deliveryPricePerMinute: centsToEuros(settings?.deliveryPricePerMinuteCents, 1),
            deliveryPricePerKilometer: centsToEuros(settings?.deliveryPricePerKilometerCents, 0.65),
            deliveryPricingBase: centsToEuros(settings?.deliveryPricingBaseCents, 0),
            deliveryPricingRounding: centsToEuros(settings?.deliveryPricingRoundingCents, 5),
            deliveryPricingMinimum: centsToEuros(settings?.deliveryPricingMinimumCents, 0),
            deliverySetupSupplement: centsToEuros(settings?.deliverySetupSupplementCents, 0),
            useTravelTimeForDeliveryCalendarEvents: settings?.useTravelTimeForDeliveryCalendarEvents ?? false,
            deliveryDefaultOriginKey: settings?.deliveryDefaultOriginKey ?? "auto",
            fuelSurchargeEnabled: settings?.fuelSurchargeEnabled ?? true,
            fuelType: settings?.fuelType ?? "gazole",
            fuelConsumption: settings?.fuelConsumptionLitersPer100Km ?? 8,
            fuelReferencePrice: centsToEuros(settings?.fuelReferencePriceCents, 2.3),
            paymentFeeConfigurations: settings?.paymentFeeConfigurations ?? [],
            enableAdvancePaymentBankTransferInstruction: settings?.enableAdvancePaymentBankTransferInstruction ?? true,
            advancePaymentBankTransferThresholdDays: settings?.advancePaymentBankTransferThresholdDays ?? 15,
            advancePaymentBankTransferBeneficiary: settings?.advancePaymentBankTransferBeneficiary ?? "",
            advancePaymentBankTransferIban: settings?.advancePaymentBankTransferIban ?? "",
            advancePaymentBankTransferBic: settings?.advancePaymentBankTransferBic ?? "",
            advancePaymentBankTransferBankName: settings?.advancePaymentBankTransferBankName ?? "",
            advancePaymentBankTransferReferenceTemplate: normalizeAdvancePaymentBankTransferReferenceTemplate(settings?.advancePaymentBankTransferReferenceTemplate),
            advancePaymentBankTransferInstructionTemplate: settings?.advancePaymentBankTransferInstructionTemplate ?? "Merci de régler l'acompte de {{advanceAmount}} par virement instantané sur le compte bancaire indiqué ci-dessous. Indiquez impérativement \"{{transferReference}}\" dans le motif du virement afin que le paiement soit rattaché à la location.",
            enableUpcomingRentalPushNotification: settings?.enableUpcomingRentalPushNotification ?? false,
            upcomingRentalNotificationLeadTimeHours: settings?.upcomingRentalNotificationLeadTimeHours ?? 6,
            pushoverUser: settings?.pushoverUser ?? "",
            pushoverToken: settings?.pushoverToken ?? "",
            pushoverSound: settings?.pushoverSound ?? "pushover",
            merchandisePurchaseReminderHorizonDays: settings?.merchandisePurchaseReminderHorizonDays ?? 14,
            defaultMerchandiseUnit: settings?.defaultMerchandiseUnit ?? "pièce",
            defaultMerchandiseReorderThreshold: settings?.defaultMerchandiseReorderThreshold ?? 0,
            autoDeductMerchandiseStockOnRental: settings?.autoDeductMerchandiseStockOnRental ?? true,
            autoRestoreMerchandiseStockOnCancellation: settings?.autoRestoreMerchandiseStockOnCancellation ?? true,
            contractTemplateBody: settings?.contractTemplateBody ?? "",
            contractTemplateHeader: settings?.contractTemplateHeader ?? "",
            contractTemplateFooter: settings?.contractTemplateFooter ?? "",
            defaultContractTemplateBody: settings?.defaultContractTemplateBody ?? "",
            defaultContractTemplateHeader: settings?.defaultContractTemplateHeader ?? "",
            defaultContractTemplateFooter: settings?.defaultContractTemplateFooter ?? "",
            clientEmailTemplates,
            clientEmailConditionalTexts: settings?.clientEmailConditionalTexts ?? []
        },
        validate: (data) => {
            const errors = {};
            if (!data.allowedDepositTypes.length) {
                errors.allowedDepositTypes = "Sélectionnez au moins une méthode.";
            }
            if (data.defaultDepositType !== NO_DEFAULT_DEPOSIT_TYPE && !data.allowedDepositTypes.includes(data.defaultDepositType)) {
                errors.defaultDepositType = "La méthode par défaut doit être autorisée.";
            }
            if (!data.defaultMerchandiseUnit?.trim()) {
                errors.defaultMerchandiseUnit = "Indiquez une unité par défaut.";
            }
            if (data.googleCalendarSyncEnabled) {
                if (!data.googleCalendarId?.trim()) {
                    errors.googleCalendarId = "Indiquez l'identifiant de l'agenda.";
                }
                if (!data.googleCalendarTimeZone?.trim()) {
                    errors.googleCalendarTimeZone = "Indiquez le fuseau horaire.";
                }
                if (!data.googleCalendarServiceAccountJson?.trim()) {
                    errors.googleCalendarServiceAccountJson = "Collez le JSON du service account.";
                } else {
                    try {
                        JSON.parse(data.googleCalendarServiceAccountJson);
                    } catch {
                        errors.googleCalendarServiceAccountJson = "Le JSON du service account est invalide.";
                    }
                }
            }
            return errors;
        },
        onSubmit: (data) => {
            const formData = new FormData();
            formData.set("rentalBufferBeforeMinutes", data.rentalBufferBeforeMinutes);
            formData.set("rentalBufferAfterMinutes", data.rentalBufferAfterMinutes);
            formData.set("rentalCalendarEventDurationMinutes", data.rentalCalendarEventDurationMinutes);
            formData.set("rentalDeliveryCalendarEventDurationMinutes", data.rentalDeliveryCalendarEventDurationMinutes);
            formData.set("rentalDeliveryWithSetupCalendarEventDurationMinutes", data.rentalDeliveryWithSetupCalendarEventDurationMinutes);
            formData.set("rentalPickupCalendarEventDurationMinutes", data.rentalPickupCalendarEventDurationMinutes);
            formData.set("googleCalendarSyncEnabled", data.googleCalendarSyncEnabled ? "true" : "false");
            formData.set("googleCalendarId", data.googleCalendarId ?? "");
            formData.set("googleCalendarTimeZone", data.googleCalendarTimeZone ?? "Europe/Paris");
            formData.set("googleCalendarServiceAccountJson", data.googleCalendarServiceAccountJson ?? "");
            data.allowedDepositTypes.forEach((type) => formData.append("allowedDepositTypes", type));
            formData.set("defaultDepositType", data.defaultDepositType);
            formData.set("requireElectronicSignatureByDefault", data.requireElectronicSignatureByDefault ? "true" : "false");
            formData.set("defaultAdvancePaymentPercent", data.defaultAdvancePaymentPercent);
            formData.set("advancePaymentReminderDelayDays", data.advancePaymentReminderDelayDays);
            formData.set("autoSendDepositReminder", data.autoSendDepositReminder ? "true" : "false");
            formData.set("depositReminderDelayHours", data.depositReminderDelayHours);
            formData.set("enablePublicAdvancePayment", data.enablePublicAdvancePayment ? "true" : "false");
            formData.set("enablePublicRentalPayment", data.enablePublicRentalPayment ? "true" : "false");
            formData.set("enableSatisfactionSurvey", data.enableSatisfactionSurvey ? "true" : "false");
            formData.set("deliveryPricingMode", data.deliveryPricingMode || "Best");
            formData.set("deliveryPricePerMinuteCents", eurosToCents(data.deliveryPricePerMinute));
            formData.set("deliveryPricePerKilometerCents", eurosToCents(data.deliveryPricePerKilometer));
            formData.set("deliveryPricingBaseCents", eurosToCents(data.deliveryPricingBase));
            formData.set("deliveryPricingRoundingCents", eurosToCents(data.deliveryPricingRounding));
            formData.set("deliveryPricingMinimumCents", eurosToCents(data.deliveryPricingMinimum));
            formData.set("deliverySetupSupplementCents", eurosToCents(data.deliverySetupSupplement));
            formData.set("useTravelTimeForDeliveryCalendarEvents", data.useTravelTimeForDeliveryCalendarEvents ? "true" : "false");
            formData.set("deliveryDefaultOriginKey", data.deliveryDefaultOriginKey || "auto");
            formData.set("fuelSurchargeEnabled", data.fuelSurchargeEnabled ? "true" : "false");
            formData.set("fuelType", data.fuelType || "gazole");
            formData.set("fuelConsumptionLitersPer100Km", Number(data.fuelConsumption || 0));
            formData.set("fuelReferencePriceCents", eurosToCents(data.fuelReferencePrice));
            formData.set("paymentFeeConfigurationsJson", JSON.stringify(data.paymentFeeConfigurations ?? []));
            formData.set("enableAdvancePaymentBankTransferInstruction", data.enableAdvancePaymentBankTransferInstruction ? "true" : "false");
            formData.set("advancePaymentBankTransferThresholdDays", data.advancePaymentBankTransferThresholdDays);
            formData.set("advancePaymentBankTransferBeneficiary", data.advancePaymentBankTransferBeneficiary ?? "");
            formData.set("advancePaymentBankTransferIban", data.advancePaymentBankTransferIban ?? "");
            formData.set("advancePaymentBankTransferBic", data.advancePaymentBankTransferBic ?? "");
            formData.set("advancePaymentBankTransferBankName", data.advancePaymentBankTransferBankName ?? "");
            formData.set("advancePaymentBankTransferReferenceTemplate", data.advancePaymentBankTransferReferenceTemplate ?? "");
            formData.set("advancePaymentBankTransferInstructionTemplate", data.advancePaymentBankTransferInstructionTemplate ?? "");
            formData.set("enableUpcomingRentalPushNotification", data.enableUpcomingRentalPushNotification ? "true" : "false");
            formData.set("upcomingRentalNotificationLeadTimeHours", data.upcomingRentalNotificationLeadTimeHours);
            formData.set("pushoverUser", data.pushoverUser);
            formData.set("pushoverToken", data.pushoverToken);
            formData.set("pushoverSound", data.pushoverSound);
            formData.set("merchandisePurchaseReminderHorizonDays", data.merchandisePurchaseReminderHorizonDays);
            formData.set("defaultMerchandiseUnit", data.defaultMerchandiseUnit);
            formData.set("defaultMerchandiseReorderThreshold", data.defaultMerchandiseReorderThreshold);
            formData.set("autoDeductMerchandiseStockOnRental", data.autoDeductMerchandiseStockOnRental ? "true" : "false");
            formData.set("autoRestoreMerchandiseStockOnCancellation", data.autoRestoreMerchandiseStockOnCancellation ? "true" : "false");
            formData.set("contractTemplateBody", data.contractTemplateBody ?? "");
            formData.set("contractTemplateHeader", data.contractTemplateHeader ?? "");
            formData.set("contractTemplateFooter", data.contractTemplateFooter ?? "");
            formData.set("clientEmailTemplatesJson", JSON.stringify(data.clientEmailTemplates ?? []));
            formData.set("clientEmailConditionalTextsJson", JSON.stringify(data.clientEmailConditionalTexts ?? []));
            submit(formData, { method: "post" });
        }
    });

    const isInvalid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const contractWordingTexts = formik.values.clientEmailConditionalTexts.filter((text) => contractWordingConditionalKeys.has(text.key));
    const areAllContractWordingCardsExpanded = contractWordingTexts.length > 0 && contractWordingTexts.every((text) => expandedContractWordingCards[text.key]);
    const setAllContractWordingCardsExpanded = (expanded) => setExpandedContractWordingCards(
        Object.fromEntries(contractWordingTexts.map((text) => [text.key, expanded]))
    );
    const allowedDepositTypes = formik.values.allowedDepositTypes;
    const settingsSaved = searchParams.get("saved") === "1";
    const defaultDepositOptions = [
        { label: "Aucune méthode par défaut", value: NO_DEFAULT_DEPOSIT_TYPE },
        ...depositOptions.filter((option) => allowedDepositTypes.includes(option.value))
    ];
    const comingSoonTooltip = "Ce paramètre sera bientôt implémenté côté métier.";
    const updateDepositType = (value, checked) => {
        const next = checked
            ? [...allowedDepositTypes, value]
            : allowedDepositTypes.filter((type) => type !== value);
        formik.setFieldValue("allowedDepositTypes", next);
        if (formik.values.defaultDepositType !== NO_DEFAULT_DEPOSIT_TYPE && !next.includes(formik.values.defaultDepositType)) {
            formik.setFieldValue("defaultDepositType", NO_DEFAULT_DEPOSIT_TYPE);
        }
    };
    const updateEmailTemplate = (index, field, value) => {
        const next = [...formik.values.clientEmailTemplates];
        next[index] = { ...next[index], [field]: value };
        formik.setFieldValue("clientEmailTemplates", next);
    };
    const updateConditionalEmailText = (index, value) => {
        const next = [...formik.values.clientEmailConditionalTexts];
        next[index] = { ...next[index], body: value };
        formik.setFieldValue("clientEmailConditionalTexts", next);
    };
    const getSelectedConditionalTextKeyList = () => Object.values(selectedConditionalTextKeys).filter(Boolean);
    const updateSelectedConditionalTextKey = (groupId, value) => {
        setSelectedConditionalTextKeys((current) => ({
            ...current,
            [groupId]: value
        }));
    };
    const testEmailTemplateAction = async (event, key, options = {}) => {
        event?.preventDefault();
        event?.stopPropagation();
        setTestingEmailTemplateKey(key);

        try {
            const response = await testClientEmailTemplate(key, {
                clientEmailTemplates: formik.values.clientEmailTemplates,
                clientEmailConditionalTexts: formik.values.clientEmailConditionalTexts,
                selectedConditionalTextKeys: options.withSelectedConditions ? getSelectedConditionalTextKeyList() : []
            });
            send("success", response?.message || "Mail de test envoyé à votre adresse.");
        } catch (error) {
            send("warning", error.message || "Impossible d'envoyer le mail de test.");
        } finally {
            setTestingEmailTemplateKey(null);
            notify();
        }
    };
    const testContractScenarioAction = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        const previewWindow = window.open("", "_blank");
        if (previewWindow) {
            previewWindow.document.write("<title>Contrat de test</title><p>Génération du contrat de test…</p>");
        }
        setTestingContractScenario("combined");

        try {
            const result = await testContractTemplate({
                selectedScenarios: Object.values(selectedContractTestScenarios),
                clientEmailConditionalTexts: formik.values.clientEmailConditionalTexts,
                contractTemplateBody: formik.values.contractTemplateBody,
                contractTemplateHeader: formik.values.contractTemplateHeader,
                contractTemplateFooter: formik.values.contractTemplateFooter
            });
            if (!result) {
                previewWindow?.close();
                return;
            }

            const url = window.URL.createObjectURL(result.blob);
            if (previewWindow) {
                previewWindow.location.replace(url);
            } else {
                const link = document.createElement("a");
                link.href = url;
                link.download = result.filename;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            }
        } catch (error) {
            previewWindow?.close();
            send("warning", error.message || "Impossible de générer le contrat de test.");
        } finally {
            setTestingContractScenario(null);
            notify();
        }
    };
    const testUpcomingRentalNotificationAction = async () => {
        setTestingUpcomingRentalNotification(true);

        try {
            const response = await testUpcomingRentalNotification();
            send("success", response?.message || "Notification de test envoyee.");
        } catch (error) {
            send("warning", error.message || "Impossible d'envoyer la notification de test.");
        } finally {
            setTestingUpcomingRentalNotification(false);
            notify();
        }
    };
    const normalizeSearch = (value) => (value ?? "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    const matchesSearch = (query, ...values) => {
        const normalizedQuery = normalizeSearch(query);
        if (!normalizedQuery) {
            return true;
        }

        return values.some((value) => normalizeSearch(value).includes(normalizedQuery));
    };
    const getVariableAutocompleteState = (fieldId, value, cursorPosition) => {
        const beforeCursor = (value ?? "").slice(0, cursorPosition);
        const start = beforeCursor.lastIndexOf("{{");
        if (start < 0) {
            return null;
        }

        const query = beforeCursor.slice(start + 2);
        if (query.includes("}") || query.includes("\n") || query.includes("\r") || query.includes(" ")) {
            return null;
        }

        return {
            fieldId,
            query,
            start,
            end: cursorPosition,
            selectedIndex: 0
        };
    };
    const syncVariableAutocomplete = (fieldId, value, target) => {
        const cursorPosition = target?.selectionStart ?? (value ?? "").length;
        setVariableAutocomplete(getVariableAutocompleteState(fieldId, value, cursorPosition));
    };
    const getVariableAutocompleteOptions = () => {
        if (!variableAutocomplete) {
            return [];
        }

        const query = variableAutocomplete.query.toLowerCase();
        const wordingKey = variableAutocomplete.fieldId.startsWith("contract-wording-")
            ? variableAutocomplete.fieldId.slice("contract-wording-".length)
            : null;
        const variables = wordingKey
            ? (contractWordingVariables[wordingKey] ?? [])
            : variableAutocomplete.fieldId.startsWith("contract-")
                ? contractTemplateVariables.map((variable) => ({ token: `{{${variable.name}}}`, description: variable.description }))
                : emailTemplateVariables;
        return variables
            .filter((variable) => variable.token.toLowerCase().includes(query))
            .slice(0, 8);
    };
    const insertContractVariable = (fieldName, variableName) => {
        const fieldId = `contract-${fieldName}`;
        const field = variableFieldRefs.current[fieldId];
        const value = formik.values[fieldName] ?? "";
        const start = field?.selectionStart ?? value.length;
        const end = field?.selectionEnd ?? start;
        const token = `{{${variableName}}}`;
        const nextValue = `${value.slice(0, start)}${token}${value.slice(end)}`;
        formik.setFieldValue(fieldName, nextValue);
        window.setTimeout(() => {
            field?.focus();
            field?.setSelectionRange?.(start + token.length, start + token.length);
        }, 0);
    };
    const insertContractWordingVariable = (index, key, token) => {
        const fieldId = `contract-wording-${key}`;
        const field = variableFieldRefs.current[fieldId];
        const value = formik.values.clientEmailConditionalTexts[index]?.body ?? "";
        const start = field?.selectionStart ?? value.length;
        const end = field?.selectionEnd ?? start;
        const nextValue = `${value.slice(0, start)}${token}${value.slice(end)}`;
        updateConditionalEmailText(index, nextValue);
        window.setTimeout(() => {
            field?.focus();
            field?.setSelectionRange?.(start + token.length, start + token.length);
        }, 0);
    };
    const updateVariableAutocompleteSelection = (direction) => {
        const options = getVariableAutocompleteOptions();
        if (!variableAutocomplete || !options.length) {
            return;
        }

        const nextIndex = (variableAutocomplete.selectedIndex + direction + options.length) % options.length;
        setVariableAutocomplete({ ...variableAutocomplete, selectedIndex: nextIndex });
    };
    const insertEmailVariable = (token, value, onValueChange) => {
        if (!variableAutocomplete) {
            return;
        }

        const nextValue = `${value.slice(0, variableAutocomplete.start)}${token}${value.slice(variableAutocomplete.end)}`;
        const cursorPosition = variableAutocomplete.start + token.length;
        onValueChange(nextValue);
        setVariableAutocomplete(null);

        window.setTimeout(() => {
            const field = variableFieldRefs.current[variableAutocomplete.fieldId];
            field?.focus();
            field?.setSelectionRange?.(cursorPosition, cursorPosition);
        }, 0);
    };
    const handleVariableAutocompleteKeyDown = (event, value, onValueChange) => {
        if (!variableAutocomplete) {
            return;
        }

        const options = getVariableAutocompleteOptions();
        if (!options.length) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            updateVariableAutocompleteSelection(1);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            updateVariableAutocompleteSelection(-1);
        } else if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            insertEmailVariable(options[variableAutocomplete.selectedIndex]?.token ?? options[0].token, value, onValueChange);
        } else if (event.key === "Escape") {
            event.preventDefault();
            setVariableAutocomplete(null);
        }
    };
    const renderVariableAutocomplete = (fieldId, value, onValueChange) => {
        if (variableAutocomplete?.fieldId !== fieldId) {
            return null;
        }

        const options = getVariableAutocompleteOptions();
        if (!options.length) {
            return null;
        }

        return (
            <div className="settings-email-autocomplete" role="listbox">
                {options.map((variable, optionIndex) => (
                    <button
                        type="button"
                        key={variable.token}
                        className={classNames("settings-email-autocomplete__option", {
                            "settings-email-autocomplete__option--selected": optionIndex === variableAutocomplete.selectedIndex,
                            "settings-email-variable-help": true
                        })}
                        data-pr-tooltip={variable.description}
                        onMouseDown={(event) => {
                            event.preventDefault();
                            insertEmailVariable(variable.token, value, onValueChange);
                        }}
                    >
                        <code className="settings-email-variable-token">{variable.token}</code>
                    </button>
                ))}
            </div>
        );
    };
    const renderDetectedEmailVariables = (value) => {
        const tokens = Array.from(new Set((value ?? "").match(/\{\{[^}\s]+\}\}/g) ?? []));
        if (!tokens.length) {
            return null;
        }

        return (
            <div className="settings-email-detected-variables">
                {tokens.map((token) => {
                    const variable = emailTemplateVariables.find((item) => item.token === token);
                    return (
                        <code
                            className={classNames("settings-email-variable-token", {
                                "settings-email-variable-token--unknown": !variable,
                                "settings-email-variable-help": true
                            })}
                            data-pr-tooltip={variable?.description ?? "Variable non reconnue."}
                            key={token}
                        >
                            {token}
                        </code>
                    );
                })}
            </div>
        );
    };
    const renderConditionalEmailTextEditor = ({ text, index }) => (
        <label className="settings-number-field settings-email-conditional-item" key={text.key}>
            <span className="settings-email-conditional-item__header">
                <span>{conditionalEmailTextConditionLabels[text.key] ?? text.label}</span>
                <code>{text.key}</code>
            </span>
            <small>{text.description}</small>
            <span className="settings-email-autocomplete-field">
                <InputTextarea
                    ref={(element) => {
                        variableFieldRefs.current[`conditional-${index}-body`] = element?.element ?? element;
                    }}
                    value={text.body ?? ""}
                    onChange={(e) => {
                        updateConditionalEmailText(index, e.target.value);
                        syncVariableAutocomplete(`conditional-${index}-body`, e.target.value, e.target);
                    }}
                    onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, text.body ?? "", (value) => updateConditionalEmailText(index, value))}
                    onKeyUp={(e) => syncVariableAutocomplete(`conditional-${index}-body`, e.target.value, e.target)}
                    onClick={(e) => syncVariableAutocomplete(`conditional-${index}-body`, e.target.value, e.target)}
                    onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)}
                    rows={3}
                    autoResize
                />
                {renderVariableAutocomplete(`conditional-${index}-body`, text.body ?? "", (value) => updateConditionalEmailText(index, value))}
            </span>
            {renderDetectedEmailVariables(text.body)}
        </label>
    );
    const filteredEmailVariables = emailTemplateVariables.filter((variable) =>
        matchesSearch(emailVariableSearch, variable.token, variable.description)
    );
    const filteredConditionalEmailTexts = formik.values.clientEmailConditionalTexts
        .map((text, index) => ({ text, index }))
        .filter(({ text }) => !contractConditionalTextKeys.has(text.key))
        .filter(({ text }) => matchesSearch(conditionalTextSearch, text.label, text.description, text.key, text.body));
    const emailConditionalTextCount = formik.values.clientEmailConditionalTexts
        .filter((text) => !contractConditionalTextKeys.has(text.key)).length;
    const filteredConditionalEmailTextByKey = new Map(filteredConditionalEmailTexts.map((entry) => [entry.text.key, entry]));
    const knownConditionalEmailTextKeys = new Set(conditionalEmailTextGroups.flatMap((group) => group.keys));
    const filteredConditionalEmailTextGroups = conditionalEmailTextGroups
        .map((group) => ({
            ...group,
            items: group.keys
                .map((key) => filteredConditionalEmailTextByKey.get(key))
                .filter(Boolean)
        }))
        .filter((group) => group.items.length);
    const filteredUnknownConditionalEmailTexts = filteredConditionalEmailTexts.filter(({ text }) => !knownConditionalEmailTextKeys.has(text.key));
    const filteredClientEmailTemplates = formik.values.clientEmailTemplates
        .map((template, index) => ({ template, index }))
        .filter(({ template }) => matchesSearch(emailTemplateSearch, template.label, template.key, template.subject, template.body));
    const clientEmailTemplateTestOptions = formik.values.clientEmailTemplates.map((template) => ({
        label: template.label,
        value: template.key
    }));

    // Prix du carburant du jour autour de l'adresse du compte (source officielle prix-carburants.gouv.fr).
    const [fuelQuote, setFuelQuote] = React.useState(null);
    const [fuelQuoteLoading, setFuelQuoteLoading] = React.useState(false);
    React.useEffect(() => {
        let active = true;
        setFuelQuoteLoading(true);
        getDeliveryFuelPrice(formik.values.fuelType)
            .then((response) => { if (active) setFuelQuote(response && response.success !== false ? response : null); })
            .catch(() => { if (active) setFuelQuote(null); })
            .finally(() => { if (active) setFuelQuoteLoading(false); });
        return () => { active = false; };
    }, [formik.values.fuelType]);

    // Exemple vivant du tarif de livraison (trajet simple de 42 min et 37,5 km).
    const deliveryExamplePerMinuteCents = computeDeliveryLegExampleCents(formik.values, "PerMinute", 42);
    const deliveryExamplePerKilometerCents = computeDeliveryLegExampleCents(formik.values, "PerKilometer", 37.5);
    const deliveryExampleRecommendedMode = chooseRecommendedDeliveryMode(formik.values.deliveryPricingMode, deliveryExamplePerMinuteCents, deliveryExamplePerKilometerCents);
    const deliveryExampleLegCents = deliveryExampleRecommendedMode === "PerKilometer" ? deliveryExamplePerKilometerCents : deliveryExamplePerMinuteCents;
    const deliveryExampleSetupCents = eurosToCents(formik.values.deliverySetupSupplement);
    const deliveryExampleMinimumApplied = isDeliveryMinimumApplied(formik.values, "PerMinute", 42) || isDeliveryMinimumApplied(formik.values, "PerKilometer", 37.5);

    return (
        <div className="account-page settings-page">
            <Tooltip target=".settings-coming-soon" content={comingSoonTooltip} position="top" />
            <Tooltip target=".settings-email-variable-help" position="top" />
            <div className="account-page-header">
                <div>
                    <p className="account-page-kicker">Paramètres</p>
                    <h1>Paramètres</h1>
                    <p>Réglages appliqués aux prochaines locations et aux contrôles de disponibilité.</p>
                </div>
            </div>

            <Form method="post" id="thumbnail-form" className={`account-form settings-form settings-form--${activeSettingsCategory}`} onSubmit={formik.handleSubmit}>
                {settingsSaved && (
                    <div className="settings-save-message" role="status">
                        <i className="pi pi-check-circle" aria-hidden="true"></i>
                        <span>Les paramètres ont été enregistrés avec succès.</span>
                    </div>
                )}

                <nav className="settings-category-nav" aria-label="Catégories de paramètres">
                    {settingsCategories.map((category) => (
                        <button
                            type="button"
                            key={category.id}
                            className={classNames("settings-category-nav__item", { "settings-category-nav__item--active": activeSettingsCategory === category.id })}
                            onClick={() => setActiveSettingsCategory(category.id)}
                        >
                            <i className={`pi ${category.icon}`} aria-hidden="true" />
                            <span><b>{category.label}</b><small>{category.description}</small></span>
                        </button>
                    ))}
                    <button type="button" className={classNames("settings-category-nav__item settings-category-nav__item--all", { "settings-category-nav__item--active": activeSettingsCategory === "all" })} onClick={() => setActiveSettingsCategory("all")}>
                        <i className="pi pi-th-large" aria-hidden="true" />
                        <span><b>Tout afficher</b><small>Vue complète</small></span>
                    </button>
                </nav>

                <section className="account-section contract-template-section" data-settings-category="calendar">
                    <div className="account-section-header">
                        <i className="pi pi-clock" aria-hidden="true"></i>
                        <div>
                            <h2>Disponibilités et calendrier</h2>
                            <p>La marge protège uniquement le stock d'un même outil. Les durées calendrier entourent l'heure prévue, sauf quand le locataire vient retirer le matériel.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--two">
                        <label className="settings-number-field">
                            <span>Marge matériel avant location</span>
                            <InputNumber name="rentalBufferBeforeMinutes" value={formik.values.rentalBufferBeforeMinutes} onValueChange={(e) => formik.setFieldValue("rentalBufferBeforeMinutes", e.value ?? 0)} min={0} max={240} suffix=" min" />
                            <small>Rend le même outil indisponible avant la location pour prévoir nettoyage, préparation ou retard.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Marge matériel après location</span>
                            <InputNumber name="rentalBufferAfterMinutes" value={formik.values.rentalBufferAfterMinutes} onValueChange={(e) => formik.setFieldValue("rentalBufferAfterMinutes", e.value ?? 0)} min={0} max={240} suffix=" min" />
                            <small>Rend le même outil indisponible après la location. N'allonge pas les événements du calendrier.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Durée retrait / retour client sans livraison</span>
                            <InputNumber name="rentalCalendarEventDurationMinutes" value={formik.values.rentalCalendarEventDurationMinutes} onValueChange={(e) => formik.setFieldValue("rentalCalendarEventDurationMinutes", e.value ?? 30)} min={5} max={240} suffix=" min" />
                            <small>Quand le locataire vient chercher ou ramener le matériel, la durée démarre à l'heure prévue.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Durée livraison simple</span>
                            <InputNumber name="rentalDeliveryCalendarEventDurationMinutes" value={formik.values.rentalDeliveryCalendarEventDurationMinutes} onValueChange={(e) => formik.setFieldValue("rentalDeliveryCalendarEventDurationMinutes", e.value ?? 60)} min={5} max={240} suffix=" min" />
                            <small>Appliquée avant et après l'heure prévue pour une livraison sans installation.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Durée livraison avec installation</span>
                            <InputNumber name="rentalDeliveryWithSetupCalendarEventDurationMinutes" value={formik.values.rentalDeliveryWithSetupCalendarEventDurationMinutes} onValueChange={(e) => formik.setFieldValue("rentalDeliveryWithSetupCalendarEventDurationMinutes", e.value ?? 120)} min={5} max={240} suffix=" min" />
                            <small>Appliquée avant et après l'heure prévue pour livrer, installer, puis reprendre ce type de location.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Durée reprise chez le client</span>
                            <InputNumber name="rentalPickupCalendarEventDurationMinutes" value={formik.values.rentalPickupCalendarEventDurationMinutes} onValueChange={(e) => formik.setFieldValue("rentalPickupCalendarEventDurationMinutes", e.value ?? 60)} min={5} max={240} suffix=" min" />
                            <small>Appliquée avant et après l'heure prévue quand vous récupérez l'outil chez le client.</small>
                        </label>
                    </div>
                    <div className="account-section-header account-section-header--compact">
                        <i className="pi pi-google" aria-hidden="true"></i>
                        <div>
                            <h3>Google Calendar</h3>
                            <p>Synchronisez les événements et locations visibles dans Lokapp vers l'agenda Google configuré.</p>
                        </div>
                        <Button type="button" className="settings-google-help-button" icon="pi pi-question-circle" label="Comment obtenir le JSON ?" text onClick={() => setGoogleCalendarHelpVisible(true)} />
                    </div>
                    <div className="account-choice-list">
                        <label className={classNames("account-choice", { "account-choice--selected": formik.values.googleCalendarSyncEnabled })}>
                            <Checkbox name="googleCalendarSyncEnabled" checked={formik.values.googleCalendarSyncEnabled} onChange={(e) => formik.setFieldValue("googleCalendarSyncEnabled", e.checked)} />
                            <span><b>Activer la synchronisation Google Calendar</b><small>Les prochains ajouts, modifications et suppressions seront envoyés à Google.</small></span>
                        </label>
                    </div>
                    <div className="account-fields-grid account-fields-grid--two">
                        <label className="settings-number-field">
                            <span>Identifiant de l'agenda</span>
                            <InputText name="googleCalendarId" value={formik.values.googleCalendarId} onChange={(e) => formik.setFieldValue("googleCalendarId", e.target.value)} className={classNames({ "p-invalid": !!formik.errors.googleCalendarId })} placeholder="primary ou agenda@group.calendar.google.com" />
                            {formik.errors.googleCalendarId ? <small className="p-error">{formik.errors.googleCalendarId}</small> : <small>Utilisez l'identifiant Google Calendar de l'agenda cible.</small>}
                        </label>
                        <label className="settings-number-field">
                            <span>Fuseau horaire</span>
                            <InputText name="googleCalendarTimeZone" value={formik.values.googleCalendarTimeZone} onChange={(e) => formik.setFieldValue("googleCalendarTimeZone", e.target.value)} className={classNames({ "p-invalid": !!formik.errors.googleCalendarTimeZone })} placeholder="Europe/Paris" />
                            {formik.errors.googleCalendarTimeZone ? <small className="p-error">{formik.errors.googleCalendarTimeZone}</small> : <small>Format IANA, par exemple Europe/Paris.</small>}
                        </label>
                    </div>
                    <label className="settings-number-field">
                        <span>JSON du service account</span>
                        <InputTextarea name="googleCalendarServiceAccountJson" value={formik.values.googleCalendarServiceAccountJson} onChange={(e) => formik.setFieldValue("googleCalendarServiceAccountJson", e.target.value)} className={classNames({ "p-invalid": !!formik.errors.googleCalendarServiceAccountJson })} rows={8} autoResize />
                        {formik.errors.googleCalendarServiceAccountJson ? <small className="p-error">{formik.errors.googleCalendarServiceAccountJson}</small> : <small>Collez le fichier JSON Google Cloud du service account ayant accès à cet agenda.</small>}
                    </label>
                </section>

                <section className="account-section" data-settings-category="delivery">
                    <div className="account-section-header">
                        <i className="pi pi-truck" aria-hidden="true"></i>
                        <div>
                            <h2>Frais de livraison</h2>
                            <p>Tarif utilisé par le bouton « Estimer les frais » du formulaire de location : le trajet simple entre votre adresse (ou un entrepôt) et le client est calculé automatiquement, puis deux propositions sont faites, au temps et à la distance, multipliées par le nombre de passages (livraison, reprise).</p>
                            <p className="settings-delivery-vehicle-link">
                                <i className="pi pi-calendar-times" aria-hidden="true"></i>
                                <span>Votre camion n'est pas toujours disponible ? <Link to="/outils/vehicule-livraison">Planning du véhicule de livraison</Link></span>
                            </p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <label className="settings-number-field">
                            <span>Proposition présélectionnée</span>
                            <Dropdown name="deliveryPricingMode" value={formik.values.deliveryPricingMode} options={deliveryPricingModeOptions} onChange={(e) => formik.setFieldValue("deliveryPricingMode", e.value)} className="inventory-dropdown" />
                            <small>Les deux propositions restent visibles dans le formulaire ; celle-ci est cochée par défaut. « La plus avantageuse » retient la plus élevée.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Tarif par minute (trajet simple)</span>
                            <InputNumber name="deliveryPricePerMinute" value={formik.values.deliveryPricePerMinute} onValueChange={(e) => formik.setFieldValue("deliveryPricePerMinute", e.value ?? 0)} min={0} max={1000} minFractionDigits={2} maxFractionDigits={2} suffix=" €" />
                            <small>Avec 1 €, 42 minutes de route donnent 42 € avant arrondi.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Tarif par kilomètre (trajet simple)</span>
                            <InputNumber name="deliveryPricePerKilometer" value={formik.values.deliveryPricePerKilometer} onValueChange={(e) => formik.setFieldValue("deliveryPricePerKilometer", e.value ?? 0)} min={0} max={1000} minFractionDigits={2} maxFractionDigits={2} suffix=" €" />
                            <small>Avec 0,65 €, 37,5 km de route donnent 24,38 € avant arrondi.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Arrondi du prix d'un passage</span>
                            <InputNumber name="deliveryPricingRounding" value={formik.values.deliveryPricingRounding} onValueChange={(e) => formik.setFieldValue("deliveryPricingRounding", e.value ?? 0)} min={0} max={100} minFractionDigits={0} maxFractionDigits={2} suffix=" €" />
                            <small>Arrondi vers le haut : avec 5 €, 42 € deviennent 45 €. 0 pour désactiver.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Forfait fixe par passage</span>
                            <InputNumber name="deliveryPricingBase" value={formik.values.deliveryPricingBase} onValueChange={(e) => formik.setFieldValue("deliveryPricingBase", e.value ?? 0)} min={0} max={10000} minFractionDigits={0} maxFractionDigits={2} suffix=" €" />
                            <small>Chargement, manutention : ajouté avant l'arrondi.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Prix minimum d'un passage</span>
                            <InputNumber name="deliveryPricingMinimum" value={formik.values.deliveryPricingMinimum} onValueChange={(e) => formik.setFieldValue("deliveryPricingMinimum", e.value ?? 0)} min={0} max={10000} minFractionDigits={0} maxFractionDigits={2} suffix=" €" />
                            <small>Plancher appliqué aux clients très proches.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Supplément installation</span>
                            <InputNumber name="deliverySetupSupplement" value={formik.values.deliverySetupSupplement} onValueChange={(e) => formik.setFieldValue("deliverySetupSupplement", e.value ?? 0)} min={0} max={10000} minFractionDigits={0} maxFractionDigits={2} suffix=" €" />
                            <small>Ajouté une seule fois pour « Livraison avec reprise et installation ».</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Point de départ par défaut</span>
                            <Dropdown name="deliveryDefaultOriginKey" value={formik.values.deliveryDefaultOriginKey} options={deliveryOriginOptions} onChange={(e) => formik.setFieldValue("deliveryDefaultOriginKey", e.value)} className="inventory-dropdown" />
                            <small>Présélectionné dans le formulaire ; vous pouvez toujours changer de point de départ location par location.</small>
                        </label>
                    </div>
                    <div className="settings-delivery-example" aria-live="polite">
                        <i className="pi pi-calculator" aria-hidden="true"></i>
                        <span>
                            <b>Exemple pour 42 min et 37,5 km de trajet simple : au temps {formatEuros(deliveryExamplePerMinuteCents)}, à la distance {formatEuros(deliveryExamplePerKilometerCents)} par passage</b>
                            <small>
                                Proposition cochée par défaut : {deliveryExampleRecommendedMode === "PerKilometer" ? "à la distance" : "au temps"} ({formatEuros(deliveryExampleLegCents)} par passage).
                                {" "}Soit {formatEuros(deliveryExampleLegCents * 2)} pour une livraison avec reprise
                                {deliveryExampleSetupCents > 0 ? `, ${formatEuros(deliveryExampleLegCents * 2 + deliveryExampleSetupCents)} avec installation` : ""}
                                {" "}et {formatEuros(deliveryExampleLegCents)} pour une livraison ou une reprise seule.
                                {deliveryExampleMinimumApplied ? " Le prix minimum s'applique sur cet exemple : le formulaire le signale dans la proposition." : ""}
                            </small>
                        </span>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <label className="settings-number-field">
                            <span>Carburant du véhicule</span>
                            <Dropdown name="fuelType" value={formik.values.fuelType} options={fuelTypeOptions} onChange={(e) => formik.setFieldValue("fuelType", e.value)} className="inventory-dropdown" />
                            <small>
                                {fuelQuoteLoading
                                    ? "Prix du jour en cours de chargement…"
                                    : (fuelQuote
                                        ? `Prix du jour : ${formatEuros(fuelQuote.priceCents)}/L (${fuelQuote.scopeLabel}, ${fuelQuote.stationCount} stations, source prix-carburants.gouv.fr).`
                                        : "Prix du jour indisponible pour le moment.")}
                            </small>
                        </label>
                        <label className="settings-number-field">
                            <span>Consommation du véhicule</span>
                            <InputNumber name="fuelConsumption" value={formik.values.fuelConsumption} onValueChange={(e) => formik.setFieldValue("fuelConsumption", e.value ?? 0)} min={0} max={100} minFractionDigits={0} maxFractionDigits={1} suffix=" L/100 km" />
                            <small>Sert au coût carburant affiché dans chaque estimation et à l'indexation.</small>
                        </label>
                        <label className="settings-number-field">
                            <span>Prix de référence de vos tarifs</span>
                            <InputNumber name="fuelReferencePrice" value={formik.values.fuelReferencePrice} onValueChange={(e) => formik.setFieldValue("fuelReferencePrice", e.value ?? 0)} min={0} max={10} minFractionDigits={2} maxFractionDigits={3} suffix=" €/L" />
                            <small>
                                Prix du litre quand vous avez fixé vos tarifs. L'indexation ajuste chaque passage de la différence avec le prix du jour.
                                {fuelQuote && (
                                    <>
                                        {" "}
                                        <Button type="button" size="small" text label={`Utiliser ${formatEuros(fuelQuote.priceCents)}/L`} onClick={() => formik.setFieldValue("fuelReferencePrice", fuelQuote.priceCents / 100)} />
                                    </>
                                )}
                            </small>
                        </label>
                    </div>
                    <div className="settings-check-grid">
                        <label className={classNames("account-choice", { "account-choice--selected": formik.values.fuelSurchargeEnabled })}>
                            <Checkbox name="fuelSurchargeEnabled" checked={formik.values.fuelSurchargeEnabled} onChange={(e) => formik.setFieldValue("fuelSurchargeEnabled", e.checked)} />
                            <span>
                                <b>Indexer les frais sur le prix du carburant</b>
                                <small>Chaque passage est ajusté de : 2 × distance × consommation ÷ 100 × (prix du jour − prix de référence). Avec 38 km, 8 L/100 km et un gazole passé de 2,30 € à 2,50 €, cela fait +1,22 € par passage. Une baisse du carburant joue dans l'autre sens.</small>
                            </span>
                        </label>
                        <label className={classNames("account-choice", { "account-choice--selected": formik.values.useTravelTimeForDeliveryCalendarEvents })}>
                            <Checkbox name="useTravelTimeForDeliveryCalendarEvents" checked={formik.values.useTravelTimeForDeliveryCalendarEvents} onChange={(e) => formik.setFieldValue("useTravelTimeForDeliveryCalendarEvents", e.checked)} />
                            <span>
                                <b>Ajouter le temps de trajet aux événements d'agenda</b>
                                <small>Quand une estimation a été appliquée à la location, les événements Google Calendar de livraison et de reprise s'allongent du trajet aller (avant) et retour (après), en plus des durées du calendrier.</small>
                            </span>
                        </label>
                    </div>
                </section>

                <section className="account-section contract-template-section" data-settings-category="contract">
                    <div className="account-section-header">
                        <i className="pi pi-file-pdf" aria-hidden="true"></i>
                        <div>
                            <h2>Contrat de location</h2>
                            <p>Personnalisez l'intégralité du contrat. Les lignes commençant par <code>#</code> ou <code>##</code> deviennent des titres dans le PDF.</p>
                        </div>
                    </div>
                    <div className="contract-template-workspace">
                        <aside className="contract-template-sidebar">
                            <div className="contract-template-sidebar-heading">
                                <i className="pi pi-code" aria-hidden="true"></i>
                                <div><b>Variables</b><small>Survolez une variable pour comprendre sa source.</small></div>
                            </div>
                            <div className="contract-template-variable-list">
                                {contractTemplateVariableGroups.map((group) => <div className="contract-template-variable-group" key={group.label}><b>{group.label}</b><div>{group.variables.map((variable) => <button type="button" className="contract-template-variable settings-email-variable-help" key={variable.name} data-pr-tooltip={variable.description} onMouseDown={(event) => { event.preventDefault(); insertContractVariable("contractTemplateBody", variable.name); }}><code>{`{{${variable.name}}}`}</code></button>)}</div></div>)}
                            </div>
                            <small className="contract-template-pagination">Pied de page : <code>{"{{currentPage}}"}</code> · <code>{"{{totalPages}}"}</code></small>
                        </aside>
                        <div className="contract-template-document">
                            <div className="contract-template-field contract-template-field--header">
                                <div className="contract-template-field-label"><i className="pi pi-arrow-up" aria-hidden="true"></i><span>En-tête</span><small>Affiché en haut de chaque page.</small></div>
                                <span className="settings-email-autocomplete-field"><InputTextarea ref={(element) => { variableFieldRefs.current["contract-contractTemplateHeader"] = element?.element ?? element; }} value={formik.values.contractTemplateHeader} onChange={(e) => { formik.setFieldValue("contractTemplateHeader", e.target.value); syncVariableAutocomplete("contract-contractTemplateHeader", e.target.value, e.target); }} onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, formik.values.contractTemplateHeader ?? "", (value) => formik.setFieldValue("contractTemplateHeader", value))} onKeyUp={(e) => syncVariableAutocomplete("contract-contractTemplateHeader", e.target.value, e.target)} onClick={(e) => syncVariableAutocomplete("contract-contractTemplateHeader", e.target.value, e.target)} onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)} rows={3} autoResize />{renderVariableAutocomplete("contract-contractTemplateHeader", formik.values.contractTemplateHeader ?? "", (value) => formik.setFieldValue("contractTemplateHeader", value))}</span>
                            </div>
                            <div className="contract-template-field contract-template-field--body">
                                <div className="contract-template-field-label"><i className="pi pi-file-edit" aria-hidden="true"></i><span>Contenu du contrat</span><small><code>#</code> et <code>##</code> créent des titres ; <code>**texte**</code> le met en gras.</small></div>
                                <span className="settings-email-autocomplete-field"><InputTextarea ref={(element) => { variableFieldRefs.current["contract-contractTemplateBody"] = element?.element ?? element; }} value={formik.values.contractTemplateBody} onChange={(e) => { formik.setFieldValue("contractTemplateBody", e.target.value); syncVariableAutocomplete("contract-contractTemplateBody", e.target.value, e.target); }} onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, formik.values.contractTemplateBody ?? "", (value) => formik.setFieldValue("contractTemplateBody", value))} onKeyUp={(e) => syncVariableAutocomplete("contract-contractTemplateBody", e.target.value, e.target)} onClick={(e) => syncVariableAutocomplete("contract-contractTemplateBody", e.target.value, e.target)} onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)} rows={28} autoResize />{renderVariableAutocomplete("contract-contractTemplateBody", formik.values.contractTemplateBody ?? "", (value) => formik.setFieldValue("contractTemplateBody", value))}</span>
                            </div>
                            <div className="contract-template-field contract-template-field--footer">
                                <div className="contract-template-field-label"><i className="pi pi-arrow-down" aria-hidden="true"></i><span>Pied de page</span><small>Affiché en bas de chaque page.</small></div>
                                <span className="settings-email-autocomplete-field"><InputTextarea ref={(element) => { variableFieldRefs.current["contract-contractTemplateFooter"] = element?.element ?? element; }} value={formik.values.contractTemplateFooter} onChange={(e) => { formik.setFieldValue("contractTemplateFooter", e.target.value); syncVariableAutocomplete("contract-contractTemplateFooter", e.target.value, e.target); }} onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, formik.values.contractTemplateFooter ?? "", (value) => formik.setFieldValue("contractTemplateFooter", value))} onKeyUp={(e) => syncVariableAutocomplete("contract-contractTemplateFooter", e.target.value, e.target)} onClick={(e) => syncVariableAutocomplete("contract-contractTemplateFooter", e.target.value, e.target)} onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)} rows={3} autoResize />{renderVariableAutocomplete("contract-contractTemplateFooter", formik.values.contractTemplateFooter ?? "", (value) => formik.setFieldValue("contractTemplateFooter", value))}</span>
                            </div>
                        </div>
                    </div>
                    <div className="contract-template-full-width">
                            <div className="contract-template-automatic-texts">
                                <div className="contract-template-automatic-header"><i className="pi pi-sparkles" aria-hidden="true"></i><div><span>Textes automatiques</span><small>Ces textes sont ajoutés selon le contexte de chaque location.</small></div></div>
                                <div className="contract-template-automatic-group contract-template-service-terms-group"><div className="contract-template-wording-heading"><div><b>{"{{serviceTerms}}"} — Article 3bis, plateforme</b><small>Le texte de l'article 3bis se règle sur la fiche de chaque plateforme (<Link to="/services/liste">Plateformes</Link>), avec les variables <code>{"{{serviceName}}"}</code> et <code>{"{{lessorDative}}"}</code>. Choisissez une plateforme dans l'aperçu ci-dessous pour le voir dans le contrat.</small></div></div></div>
                            </div>
                            <div className="contract-template-automatic-group">
                                <div className="contract-template-wording-heading"><div><b>Clauses de la location</b><small>Le texte est entièrement modifiable. Utilisez <code>**texte**</code> pour le gras, ouvrez une carte pour modifier sa formulation et cliquez sur une variable pour l'insérer.</small></div><Button type="button" className="contract-template-wording-toggle-all" icon={areAllContractWordingCardsExpanded ? "pi pi-angle-up" : "pi pi-angle-down"} label={areAllContractWordingCardsExpanded ? "Tout replier" : "Tout déplier"} text onClick={() => setAllContractWordingCardsExpanded(!areAllContractWordingCardsExpanded)} /></div>
                                <div className="contract-template-service-terms-grid contract-template-service-terms-grid--wording">
                                    {formik.values.clientEmailConditionalTexts.map((text, index) => contractWordingConditionalKeys.has(text.key) && (
                                        <div className={classNames("contract-template-service-card", { "contract-template-service-card--collapsed": !expandedContractWordingCards[text.key] })} key={text.key}>
                                            <button type="button" className="contract-template-service-card__toggle" aria-expanded={!!expandedContractWordingCards[text.key]} onClick={() => setExpandedContractWordingCards((current) => ({ ...current, [text.key]: !current[text.key] }))}><span className="contract-template-service-card__header"><i className="pi pi-pencil" aria-hidden="true"></i><b>{text.label}</b><small>{contractWordingConditions[text.key] ?? text.description}</small></span><i className={`pi ${expandedContractWordingCards[text.key] ? "pi-chevron-up" : "pi-chevron-down"}`} aria-hidden="true"></i></button>
                                            {expandedContractWordingCards[text.key] && <><div className="contract-template-wording-variables">
                                                {(contractWordingVariables[text.key] ?? []).map((variable) => <button type="button" className="settings-email-variable-help" data-pr-tooltip={variable.description} key={variable.token} onMouseDown={(event) => { event.preventDefault(); insertContractWordingVariable(index, text.key, variable.token); }}><code>{variable.token}</code></button>)}
                                            </div>
                                            <span className="settings-email-autocomplete-field">
                                                <InputTextarea
                                                    ref={(element) => { variableFieldRefs.current[`contract-wording-${text.key}`] = element?.element ?? element; }}
                                                    value={text.body ?? ""}
                                                    onChange={(event) => { updateConditionalEmailText(index, event.target.value); syncVariableAutocomplete(`contract-wording-${text.key}`, event.target.value, event.target); }}
                                                    onKeyDown={(event) => handleVariableAutocompleteKeyDown(event, text.body ?? "", (value) => updateConditionalEmailText(index, value))}
                                                    onKeyUp={(event) => syncVariableAutocomplete(`contract-wording-${text.key}`, event.target.value, event.target)}
                                                    onClick={(event) => syncVariableAutocomplete(`contract-wording-${text.key}`, event.target.value, event.target)}
                                                    onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)}
                                                    rows={6}
                                                />
                                                {renderVariableAutocomplete(`contract-wording-${text.key}`, text.body ?? "", (value) => updateConditionalEmailText(index, value))}
                                            </span></>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="contract-template-test-panel">
                                <div className="contract-template-test-heading"><i className="pi pi-sliders-h" aria-hidden="true"></i><div><b>Tester une combinaison</b><small>Composez le contexte de la location : un seul PDF est généré avec toutes les conditions sélectionnées.</small></div></div>
                                <div className="contract-template-test-fields">
                                    {contractTestScenarioGroupsWithPlatforms.map((group) => <div className="contract-template-test-field" key={group.id}><span>{group.label}</span><Dropdown value={selectedContractTestScenarios[group.id]} options={group.options} onChange={(event) => setSelectedContractTestScenarios((current) => ({ ...current, [group.id]: event.value }))} /></div>)}
                                </div>
                                <Button type="button" className="contract-template-test-button" icon="pi pi-file-pdf" label="Générer le contrat de test" loading={testingContractScenario === "combined"} disabled={testingContractScenario !== null || isSaving} onClick={testContractScenarioAction} />
                            </div>
                            <div className="contract-template-actions">
                                <span>Le contrat déjà enregistré pour une location n’est pas modifié.</span>
                                <Button type="button" label="Restaurer le modèle de base" icon="pi pi-refresh" outlined severity="secondary" onClick={() => formik.setValues({ ...formik.values, contractTemplateHeader: formik.values.defaultContractTemplateHeader, contractTemplateBody: formik.values.defaultContractTemplateBody, contractTemplateFooter: formik.values.defaultContractTemplateFooter })} />
                            </div>
                    </div>
                </section>

                <section className="account-section" data-settings-category="stock">
                    <div className="account-section-header">
                        <i className="pi pi-box" aria-hidden="true"></i>
                        <div>
                            <h2>Stock marchandises</h2>
                            <p>Réglages appliqués aux consommables liés aux options de location.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three settings-merchandise-grid">
                        <label className="settings-number-field">
                            <span>Anticipation des achats marchandises</span>
                            <InputNumber name="merchandisePurchaseReminderHorizonDays" value={formik.values.merchandisePurchaseReminderHorizonDays} onValueChange={(e) => formik.setFieldValue("merchandisePurchaseReminderHorizonDays", e.value ?? 14)} min={1} max={120} suffix=" j" />
                            <small>Détermine jusqu'à combien de jours à l'avance les prochaines locations sont analysées pour prévoir les achats.</small>
                        </label>
                        <label className="settings-number-field settings-compact-unit-field">
                            <span>Unité par défaut</span>
                            <input name="defaultMerchandiseUnit" value={formik.values.defaultMerchandiseUnit} onChange={(e) => formik.setFieldValue("defaultMerchandiseUnit", e.target.value)} maxLength={40} className={classNames("settings-unit-input", { "p-invalid": isInvalid("defaultMerchandiseUnit") })} />
                            {isInvalid("defaultMerchandiseUnit") ? <small className="p-error">{formik.errors.defaultMerchandiseUnit}</small> : <small>Préremplie lors de la création d'une marchandise.</small>}
                        </label>
                        <label className="settings-number-field">
                            <span>Seuil d'alerte par défaut</span>
                            <InputNumber name="defaultMerchandiseReorderThreshold" value={formik.values.defaultMerchandiseReorderThreshold} onValueChange={(e) => formik.setFieldValue("defaultMerchandiseReorderThreshold", e.value ?? 0)} min={0} max={100000} />
                            <small>Prérempli sur les nouvelles marchandises.</small>
                        </label>
                    </div>
                    <div className="settings-check-grid settings-merchandise-check-grid">
                        <label className={classNames("account-choice", { "account-choice--selected": formik.values.autoDeductMerchandiseStockOnRental })}>
                            <Checkbox name="autoDeductMerchandiseStockOnRental" checked={formik.values.autoDeductMerchandiseStockOnRental} onChange={(e) => formik.setFieldValue("autoDeductMerchandiseStockOnRental", e.checked)} />
                            <span>
                                <b>Déduire automatiquement lors d'une location</b>
                                <small>Crée une sortie de stock quand une location utilise des options liées à des marchandises.</small>
                            </span>
                        </label>
                        <label className={classNames("account-choice", { "account-choice--selected": formik.values.autoRestoreMerchandiseStockOnCancellation })}>
                            <Checkbox name="autoRestoreMerchandiseStockOnCancellation" checked={formik.values.autoRestoreMerchandiseStockOnCancellation} onChange={(e) => formik.setFieldValue("autoRestoreMerchandiseStockOnCancellation", e.checked)} />
                            <span>
                                <b>Remettre en stock lors d'une annulation</b>
                                <small>Annule les sorties de marchandises d'une location annulée.</small>
                            </span>
                        </label>
                        <label className="settings-number-field settings-field-disabled settings-coming-soon" data-pr-tooltip={comingSoonTooltip}>
                            <span>Moment de sortie du stock marchandise</span>
                            <Dropdown value="creation" options={[{ label: "À l'enregistrement de la location", value: "creation" }]} disabled className="inventory-dropdown" />
                            <small>Bientôt disponible.</small>
                        </label>
                    </div>
                </section>

                <section className="account-section" data-settings-category="deposit">
                    <div className="account-section-header">
                        <i className="pi pi-shield" aria-hidden="true"></i>
                        <div>
                            <h2>Cautions</h2>
                            <p>Choisissez les méthodes proposées lors de la création d'une location.</p>
                        </div>
                    </div>
                    <div className="settings-check-grid">
                        {depositOptions.map((option) => (
                            <label key={option.value} className={classNames("account-choice", { "account-choice--selected": allowedDepositTypes.includes(option.value) })}>
                                <Checkbox name="allowedDepositTypes" value={option.value} checked={allowedDepositTypes.includes(option.value)} onChange={(e) => updateDepositType(option.value, e.checked)} />
                                <span>
                                    <b>{option.label}</b>
                                    <small>{option.value === "CB" ? "Caution en ligne via Stripe ou lien permanent." : option.value === "BankTransfer" ? "Consignes de virement et RIB locataire sur la page publique." : "Validation manuelle depuis la location."}</small>
                                </span>
                            </label>
                        ))}
                    </div>
                    {isInvalid("allowedDepositTypes") && <small className="p-error">{formik.errors.allowedDepositTypes}</small>}
                    <div className="account-fields-grid settings-inline-fields">
                        <label className="settings-number-field">
                            <span>Méthode par défaut</span>
                            <Dropdown name="defaultDepositType" value={formik.values.defaultDepositType} options={defaultDepositOptions} onChange={(e) => formik.setFieldValue("defaultDepositType", e.value)} placeholder="Choisir" className={classNames({ "p-invalid": isInvalid("defaultDepositType") })} />
                            {isInvalid("defaultDepositType") && <small className="p-error">{formik.errors.defaultDepositType}</small>}
                        </label>
                        <label className="settings-toggle-field">
                            <Checkbox name="autoSendDepositReminder" checked={formik.values.autoSendDepositReminder} onChange={(e) => formik.setFieldValue("autoSendDepositReminder", e.checked)} />
                            <span>
                                <b>Relance caution CB automatique</b>
                                <small>Envoie un rappel si la caution CB n'est pas déposée avant la location.</small>
                            </span>
                        </label>
                        <label className="settings-number-field">
                            <span>Délai avant relance caution CB</span>
                            <InputNumber name="depositReminderDelayHours" value={formik.values.depositReminderDelayHours} onValueChange={(e) => formik.setFieldValue("depositReminderDelayHours", e.value ?? 2)} min={1} max={72} suffix=" h" disabled={!formik.values.autoSendDepositReminder} />
                            <small>Le rappel part une seule fois quand la location démarre dans ce délai, si elle a été créée depuis plus de 24h. Sinon, il part 2h avant le début de la location.</small>
                        </label>
                    </div>
                </section>

                <section className="account-section" data-settings-category="notifications">
                    <div className="account-section-header">
                        <i className="pi pi-bell" aria-hidden="true"></i>
                        <div>
                            <h2>Notifications</h2>
                            <p>Recevez une notification Pushover lorsqu'une de vos locations commence bientot.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid settings-inline-fields">
                        <label className="settings-toggle-field">
                            <Checkbox name="enableUpcomingRentalPushNotification" checked={formik.values.enableUpcomingRentalPushNotification} onChange={(e) => formik.setFieldValue("enableUpcomingRentalPushNotification", e.checked)} />
                            <span>
                                <b>Notification de location imminente</b>
                            </span>
                        </label>
                        <label className="settings-number-field">
                            <span>Delai avant la location</span>
                            <InputNumber name="upcomingRentalNotificationLeadTimeHours" value={formik.values.upcomingRentalNotificationLeadTimeHours} onValueChange={(e) => formik.setFieldValue("upcomingRentalNotificationLeadTimeHours", e.value ?? 6)} min={1} max={168} suffix=" h" />
                        </label>
                        <label className="settings-number-field">
                            <span>Utilisateur Pushover</span>
                            <InputText name="pushoverUser" value={formik.values.pushoverUser} onChange={(e) => formik.setFieldValue("pushoverUser", e.target.value)} />
                        </label>
                        <label className="settings-number-field">
                            <span>Cle applicative Pushover</span>
                            <InputText name="pushoverToken" type="password" value={formik.values.pushoverToken} onChange={(e) => formik.setFieldValue("pushoverToken", e.target.value)} />
                        </label>
                        <label className="settings-number-field">
                            <span>Son Pushover</span>
                            <Dropdown name="pushoverSound" value={formik.values.pushoverSound} options={pushoverSoundOptions} onChange={(e) => formik.setFieldValue("pushoverSound", e.value)} />
                        </label>
                        <div className="settings-notification-test">
                            <Button
                                type="button"
                                icon="pi pi-send"
                                label="Tester la notification"
                                loading={testingUpcomingRentalNotification}
                                disabled={testingUpcomingRentalNotification || isSaving}
                                onClick={testUpcomingRentalNotificationAction}
                            />
                        </div>
                    </div>
                </section>

                <section className="account-section" data-settings-category="payment">
                    <div className="account-section-header">
                        <i className="pi pi-send" aria-hidden="true"></i>
                        <div>
                            <h2>Contrat et acompte</h2>
                            <p>Réglages par défaut appliqués aux nouvelles locations.</p>
                        </div>
                    </div>
                    <div className="account-fields-grid account-fields-grid--three">
                        <label className="settings-toggle-field">
                            <Checkbox name="requireElectronicSignatureByDefault" checked={formik.values.requireElectronicSignatureByDefault} onChange={(e) => formik.setFieldValue("requireElectronicSignatureByDefault", e.checked)} />
                            <span>
                                <b>Signature électronique par défaut</b>
                                <small>Active SignNow sur les nouvelles locations.</small>
                            </span>
                        </label>
                        <label className="settings-number-field">
                            <span>Acompte par défaut</span>
                            <InputNumber name="defaultAdvancePaymentPercent" value={formik.values.defaultAdvancePaymentPercent} onValueChange={(e) => formik.setFieldValue("defaultAdvancePaymentPercent", e.value ?? 0)} min={0} max={100} suffix=" %" />
                            <small>Pourcentage prérempli à la création.</small>
                        </label>
                        <label className="settings-toggle-field">
                            <Checkbox name="enablePublicAdvancePayment" checked={formik.values.enablePublicAdvancePayment} onChange={(e) => formik.setFieldValue("enablePublicAdvancePayment", e.checked)} />
                            <span>
                                <b>Paiement de l'acompte sur la page publique</b>
                                <small>Affiche le bouton de dépôt d'acompte et autorise le paiement en ligne de l'acompte.</small>
                            </span>
                        </label>
                        <label className="settings-toggle-field">
                            <Checkbox name="enablePublicRentalPayment" checked={formik.values.enablePublicRentalPayment} onChange={(e) => formik.setFieldValue("enablePublicRentalPayment", e.checked)} />
                            <span>
                                <b>Paiement du solde sur la page publique</b>
                                <small>Affiche le bouton de règlement du solde et autorise le paiement en ligne de la location hors acompte.</small>
                            </span>
                        </label>
                        <label className="settings-toggle-field">
                            <Checkbox name="enableSatisfactionSurvey" checked={formik.values.enableSatisfactionSurvey} onChange={(e) => formik.setFieldValue("enableSatisfactionSurvey", e.checked)} />
                            <span>
                                <b>Questionnaire de satisfaction</b>
                                <small>À la clôture de chaque location, le locataire reçoit un court questionnaire (questions générales + questions propres au matériel, modifiables depuis la fiche de l'outil). Les résultats sont visibles dans les statistiques et sur chaque location.</small>
                            </span>
                        </label>
                        <label className="settings-number-field settings-field-disabled settings-coming-soon" data-pr-tooltip={comingSoonTooltip}>
                            <span>Délai avant relance acompte</span>
                            <InputNumber name="advancePaymentReminderDelayDays" value={formik.values.advancePaymentReminderDelayDays} disabled min={0} max={30} suffix=" j" />
                            <small>Bientôt disponible.</small>
                        </label>
                    </div>
                    <div className="payment-fee-settings">
                        <div className="payment-fee-settings__header">
                            <div>
                                <h3>Instruction de virement pour acompte lointain</h3>
                                <p>Affichée quand une location demande un acompte hors plateforme et commence après le seuil configuré.</p>
                            </div>
                        </div>
                        <div className="account-fields-grid account-fields-grid--three">
                            <label className="settings-toggle-field">
                                <Checkbox name="enableAdvancePaymentBankTransferInstruction" checked={formik.values.enableAdvancePaymentBankTransferInstruction} onChange={(e) => formik.setFieldValue("enableAdvancePaymentBankTransferInstruction", e.checked)} />
                                <span>
                                    <b>Demander un virement instantané</b>
                                    <small>Ajoute l'instruction dans la fiche location et les emails d'acompte si l'acompte est marqué hors plateforme.</small>
                                </span>
                            </label>
                            <label className="settings-number-field">
                                <span>Seuil avant début de location</span>
                                <InputNumber name="advancePaymentBankTransferThresholdDays" value={formik.values.advancePaymentBankTransferThresholdDays} onValueChange={(e) => formik.setFieldValue("advancePaymentBankTransferThresholdDays", e.value ?? 15)} min={0} max={365} suffix=" j" disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                                <small>La règle s'applique si le début est strictement supérieur à ce nombre de jours.</small>
                            </label>
                            <label className="settings-number-field">
                                <span>Motif du virement</span>
                                <InputText name="advancePaymentBankTransferReferenceTemplate" value={formik.values.advancePaymentBankTransferReferenceTemplate} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferReferenceTemplate", e.target.value)} disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                                <small>Variables : {"{{rentalPublicId}}"}, {"{{advanceAmount}}"}</small>
                            </label>
                            <label className="settings-number-field">
                                <span>Bénéficiaire</span>
                                <InputText name="advancePaymentBankTransferBeneficiary" value={formik.values.advancePaymentBankTransferBeneficiary} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferBeneficiary", e.target.value)} disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                            </label>
                            <label className="settings-number-field">
                                <span>IBAN</span>
                                <InputText name="advancePaymentBankTransferIban" value={formik.values.advancePaymentBankTransferIban} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferIban", e.target.value)} disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                            </label>
                            <label className="settings-number-field">
                                <span>BIC</span>
                                <InputText name="advancePaymentBankTransferBic" value={formik.values.advancePaymentBankTransferBic} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferBic", e.target.value)} disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                            </label>
                            <label className="settings-number-field">
                                <span>Banque</span>
                                <InputText name="advancePaymentBankTransferBankName" value={formik.values.advancePaymentBankTransferBankName} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferBankName", e.target.value)} disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                            </label>
                            <label className="settings-number-field settings-email-template-editor-field">
                                <span>Texte client</span>
                                <InputTextarea name="advancePaymentBankTransferInstructionTemplate" value={formik.values.advancePaymentBankTransferInstructionTemplate} onChange={(e) => formik.setFieldValue("advancePaymentBankTransferInstructionTemplate", e.target.value)} rows={4} autoResize disabled={!formik.values.enableAdvancePaymentBankTransferInstruction} />
                                <small>Variables : {"{{advanceAmount}}"}, {"{{transferReference}}"}, {"{{bankAccountDetails}}"}, {"{{rentalPublicId}}"}</small>
                            </label>
                        </div>
                    </div>
                    <div className="payment-fee-settings">
                        <div className="payment-fee-settings__header">
                            <div>
                                <h3>Frais des services d'encaissement</h3>
                                <p>Ajoutez les commissions appliquées par vos plateformes. Elles servent à recommander le service qui vous laisse le plus sur chaque location.</p>
                            </div>
                            <Button
                                type="button"
                                label="Ajouter un service"
                                icon="pi pi-plus"
                                size="small"
                                outlined
                                onClick={() => formik.setFieldValue("paymentFeeConfigurations", [
                                    ...(formik.values.paymentFeeConfigurations ?? []),
                                    { name: "", percentage: 0, fixedFeeCents: 0 }
                                ])}
                            />
                        </div>
                        {formik.values.paymentFeeConfigurations?.length ? (
                            <div className="payment-fee-settings__list">
                                {formik.values.paymentFeeConfigurations.map((configuration, index) => (
                                    <div className="payment-fee-settings__row" key={index}>
                                        <label>
                                            <span>Service</span>
                                            <InputText
                                                value={configuration.name ?? ""}
                                                placeholder="Ex. Bricolib"
                                                onChange={(event) => formik.setFieldValue(`paymentFeeConfigurations.${index}.name`, event.target.value)}
                                            />
                                        </label>
                                        <label>
                                            <span>Commission</span>
                                            <InputNumber
                                                value={configuration.percentage ?? 0}
                                                min={0}
                                                max={100}
                                                minFractionDigits={0}
                                                maxFractionDigits={3}
                                                suffix=" %"
                                                onValueChange={(event) => formik.setFieldValue(`paymentFeeConfigurations.${index}.percentage`, event.value ?? 0)}
                                            />
                                        </label>
                                        <label>
                                            <span>Frais fixes</span>
                                            <InputNumber
                                                value={(configuration.fixedFeeCents ?? 0) / 100}
                                                min={0}
                                                minFractionDigits={2}
                                                maxFractionDigits={2}
                                                suffix=" €"
                                                onValueChange={(event) => formik.setFieldValue(`paymentFeeConfigurations.${index}.fixedFeeCents`, Math.round((event.value ?? 0) * 100))}
                                            />
                                        </label>
                                        <Button
                                            type="button"
                                            icon="pi pi-trash"
                                            rounded
                                            text
                                            severity="danger"
                                            aria-label={`Supprimer ${configuration.name || "ce service"}`}
                                            onClick={() => formik.setFieldValue("paymentFeeConfigurations", formik.values.paymentFeeConfigurations.filter((_, itemIndex) => itemIndex !== index))}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="payment-fee-settings__empty">Aucun service configuré. Ajoutez vos services pour comparer leurs frais sur les locations.</p>
                        )}
                    </div>
                </section>

                <section className="account-section" data-settings-category="emails">
                    <div className="account-section-header">
                        <i className="pi pi-envelope" aria-hidden="true"></i>
                        <div>
                            <h2>Emails client</h2>
                            <p>Personnalisez les sujets et contenus des emails envoyés automatiquement aux clients.</p>
                        </div>
                    </div>
                    <div className="settings-email-help">
                        <div className="settings-email-tools-header">
                            <span>Variables disponibles</span>
                            <span>{filteredEmailVariables.length}/{emailTemplateVariables.length}</span>
                        </div>
                        <label className="settings-email-search">
                            <i className="pi pi-search" aria-hidden="true"></i>
                            <input value={emailVariableSearch} onChange={(e) => setEmailVariableSearch(e.target.value)} placeholder="Rechercher une variable" />
                        </label>
                        <div className="settings-email-variable-list">
                            {filteredEmailVariables.map((variable) => (
                                <div className="settings-email-variable settings-email-variable-help" key={variable.token} data-pr-tooltip={variable.description}>
                                    <code className="settings-email-variable-token">{variable.token}</code>
                                </div>
                            ))}
                        </div>
                        {!filteredEmailVariables.length && <p className="settings-email-empty">Aucune variable trouvée.</p>}
                    </div>
                    <details className="settings-email-template settings-email-conditional-panel">
                        <summary className="settings-email-template__header">
                            <span className="settings-email-template__title">Textes conditionnels</span>
                            <span>{filteredConditionalEmailTexts.length}/{emailConditionalTextCount}</span>
                        </summary>
                        <p className="settings-email-conditional-intro">
                            Ces textes alimentent les variables qui changent selon le contexte : heure d'envoi, caution, signature, livraison ou origine du contrat.
                        </p>
                        <details className="settings-email-conditional-test">
                            <summary className="settings-email-conditional-test__header">
                                <div>
                                    <strong>Tester un scénario conditionnel</strong>
                                    <small>Le mail de test utilisera les conditions choisies ci-dessous et sera envoyé à votre adresse.</small>
                                </div>
                            </summary>
                            <div className="settings-email-conditional-test__body">
                                <Button
                                    type="button"
                                    icon="pi pi-send"
                                    label="Tester ces conditions"
                                    loading={testingEmailTemplateKey === conditionalTestTemplateKey}
                                    disabled={testingEmailTemplateKey !== null || isSaving}
                                    onClick={(event) => testEmailTemplateAction(event, conditionalTestTemplateKey, { withSelectedConditions: true })}
                                />
                                <div className="settings-email-conditional-test__template">
                                    <div className="settings-email-conditional-test__field">
                                        <span>Mail à tester</span>
                                        <Dropdown
                                            value={conditionalTestTemplateKey}
                                            options={clientEmailTemplateTestOptions}
                                            onChange={(event) => setConditionalTestTemplateKey(event.value)}
                                        />
                                    </div>
                                </div>
                                <div className="settings-email-conditional-test__grid">
                                    {conditionalEmailTextGroups.map((group) => (
                                        <div className="settings-email-conditional-test__field" key={group.id}>
                                            <span>{group.label}</span>
                                            <Dropdown
                                                value={selectedConditionalTextKeys[group.id]}
                                                options={group.keys.map((key) => ({
                                                    label: conditionalEmailTextConditionLabels[key] ?? key,
                                                    value: key
                                                }))}
                                                onChange={(event) => updateSelectedConditionalTextKey(group.id, event.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </details>
                        <div className="settings-email-tools-bar">
                            <label className="settings-email-search">
                                <i className="pi pi-search" aria-hidden="true"></i>
                                <input value={conditionalTextSearch} onChange={(e) => setConditionalTextSearch(e.target.value)} placeholder="Rechercher un texte conditionnel" />
                            </label>
                            <span className="settings-email-tools-count">{filteredConditionalEmailTexts.length}/{emailConditionalTextCount} textes</span>
                        </div>
                        <div className="settings-email-conditional-list">
                            {filteredConditionalEmailTextGroups.map((group) => (
                                <details className="settings-email-conditional-group" key={group.id}>
                                    <summary className="settings-email-conditional-group__header">
                                        <span>
                                            <strong>{group.label}</strong>
                                            <code className="settings-email-variable-token">{group.title}</code>
                                        </span>
                                        <small>{group.description}</small>
                                    </summary>
                                    <div className="settings-email-conditional-group__items">
                                        {group.items.map(renderConditionalEmailTextEditor)}
                                    </div>
                                </details>
                            ))}
                            {!!filteredUnknownConditionalEmailTexts.length && (
                                <details className="settings-email-conditional-group">
                                    <summary className="settings-email-conditional-group__header">
                                        <span>
                                            <strong>Autres textes</strong>
                                            <code className="settings-email-variable-token">non classes</code>
                                        </span>
                                        <small>Textes conditionnels non rattaches a une variable connue.</small>
                                    </summary>
                                    <div className="settings-email-conditional-group__items">
                                        {filteredUnknownConditionalEmailTexts.map(renderConditionalEmailTextEditor)}
                                    </div>
                                </details>
                            )}
                        </div>
                        {!filteredConditionalEmailTexts.length && <p className="settings-email-empty">Aucun texte conditionnel trouvé.</p>}
                    </details>
                    <div className="settings-email-tools-bar">
                        <label className="settings-email-search">
                            <i className="pi pi-search" aria-hidden="true"></i>
                            <input value={emailTemplateSearch} onChange={(e) => setEmailTemplateSearch(e.target.value)} placeholder="Rechercher un mail" />
                        </label>
                        <span className="settings-email-tools-count">{filteredClientEmailTemplates.length}/{formik.values.clientEmailTemplates.length} mails</span>
                    </div>
                    <div className="settings-email-template-list">
                        {filteredClientEmailTemplates.map(({ template, index }) => (
                            <details className="settings-email-template" key={template.key}>
                                <summary className="settings-email-template__header">
                                    <span className="settings-email-template__title">{template.label}</span>
                                    <span className="settings-email-template__actions">
                                        <code>{template.key}</code>
                                    </span>
                                </summary>
                                <div className="settings-number-field settings-email-template-editor">
                                    <label className="settings-email-template-editor-field">
                                        <span>Objet</span>
                                        <span className="settings-email-autocomplete-field">
                                            <input
                                                ref={(element) => {
                                                    variableFieldRefs.current[`template-${index}-subject`] = element;
                                                }}
                                                value={template.subject ?? ""}
                                                onChange={(e) => {
                                                    updateEmailTemplate(index, "subject", e.target.value);
                                                    syncVariableAutocomplete(`template-${index}-subject`, e.target.value, e.target);
                                                }}
                                                onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, template.subject ?? "", (value) => updateEmailTemplate(index, "subject", value))}
                                                onKeyUp={(e) => syncVariableAutocomplete(`template-${index}-subject`, e.target.value, e.target)}
                                                onClick={(e) => syncVariableAutocomplete(`template-${index}-subject`, e.target.value, e.target)}
                                                onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)}
                                            />
                                            {renderVariableAutocomplete(`template-${index}-subject`, template.subject ?? "", (value) => updateEmailTemplate(index, "subject", value))}
                                        </span>
                                    </label>
                                    <label className="settings-email-template-editor-field">
                                        <span>Contenu</span>
                                        <span className="settings-email-template-editor-control">
                                            <span className="settings-email-autocomplete-field">
                                                <InputTextarea
                                                    ref={(element) => {
                                                        variableFieldRefs.current[`template-${index}-body`] = element?.element ?? element;
                                                    }}
                                                    value={template.body ?? ""}
                                                    onChange={(e) => {
                                                        updateEmailTemplate(index, "body", e.target.value);
                                                        syncVariableAutocomplete(`template-${index}-body`, e.target.value, e.target);
                                                    }}
                                                    onKeyDown={(e) => handleVariableAutocompleteKeyDown(e, template.body ?? "", (value) => updateEmailTemplate(index, "body", value))}
                                                    onKeyUp={(e) => syncVariableAutocomplete(`template-${index}-body`, e.target.value, e.target)}
                                                    onClick={(e) => syncVariableAutocomplete(`template-${index}-body`, e.target.value, e.target)}
                                                    onBlur={() => window.setTimeout(() => setVariableAutocomplete(null), 120)}
                                                    rows={8}
                                                    autoResize
                                                />
                                                {renderVariableAutocomplete(`template-${index}-body`, template.body ?? "", (value) => updateEmailTemplate(index, "body", value))}
                                            </span>
                                            {renderDetectedEmailVariables(template.body)}
                                        </span>
                                    </label>
                                    <div className="settings-email-template-testbar">
                                        <span>Envoyer un test a votre adresse avec une location reelle compatible.</span>
                                        <Button
                                            type="button"
                                            icon="pi pi-send"
                                            label="Tester ce mail"
                                            loading={testingEmailTemplateKey === template.key}
                                            disabled={testingEmailTemplateKey !== null || isSaving}
                                            onClick={(event) => testEmailTemplateAction(event, template.key)}
                                        />
                                    </div>
                                </div>
                            </details>
                        ))}
                    </div>
                    {!filteredClientEmailTemplates.length && <p className="settings-email-empty">Aucun mail trouvé.</p>}
                </section>

                <input type="hidden" name="rentalBufferBeforeMinutes" value={formik.values.rentalBufferBeforeMinutes} />
                <input type="hidden" name="rentalBufferAfterMinutes" value={formik.values.rentalBufferAfterMinutes} />
                <input type="hidden" name="rentalCalendarEventDurationMinutes" value={formik.values.rentalCalendarEventDurationMinutes} />
                <input type="hidden" name="rentalDeliveryCalendarEventDurationMinutes" value={formik.values.rentalDeliveryCalendarEventDurationMinutes} />
                <input type="hidden" name="rentalDeliveryWithSetupCalendarEventDurationMinutes" value={formik.values.rentalDeliveryWithSetupCalendarEventDurationMinutes} />
                <input type="hidden" name="rentalPickupCalendarEventDurationMinutes" value={formik.values.rentalPickupCalendarEventDurationMinutes} />
                <input type="hidden" name="googleCalendarSyncEnabled" value={formik.values.googleCalendarSyncEnabled ? "true" : "false"} />
                <input type="hidden" name="googleCalendarId" value={formik.values.googleCalendarId} />
                <input type="hidden" name="googleCalendarTimeZone" value={formik.values.googleCalendarTimeZone} />
                <input type="hidden" name="googleCalendarServiceAccountJson" value={formik.values.googleCalendarServiceAccountJson} />
                {formik.values.allowedDepositTypes.map((type) => (
                    <input key={type} type="hidden" name="allowedDepositTypes" value={type} />
                ))}
                <input type="hidden" name="defaultDepositType" value={formik.values.defaultDepositType} />
                <input type="hidden" name="requireElectronicSignatureByDefault" value={formik.values.requireElectronicSignatureByDefault ? "true" : "false"} />
                <input type="hidden" name="defaultAdvancePaymentPercent" value={formik.values.defaultAdvancePaymentPercent} />
                <input type="hidden" name="advancePaymentReminderDelayDays" value={formik.values.advancePaymentReminderDelayDays} />
                <input type="hidden" name="autoSendDepositReminder" value={formik.values.autoSendDepositReminder ? "true" : "false"} />
                <input type="hidden" name="depositReminderDelayHours" value={formik.values.depositReminderDelayHours} />
                <input type="hidden" name="enablePublicAdvancePayment" value={formik.values.enablePublicAdvancePayment ? "true" : "false"} />
                <input type="hidden" name="enablePublicRentalPayment" value={formik.values.enablePublicRentalPayment ? "true" : "false"} />
                <input type="hidden" name="enableSatisfactionSurvey" value={formik.values.enableSatisfactionSurvey ? "true" : "false"} />
                <input type="hidden" name="deliveryPricingMode" value={formik.values.deliveryPricingMode} />
                <input type="hidden" name="deliveryPricePerMinuteCents" value={eurosToCents(formik.values.deliveryPricePerMinute)} />
                <input type="hidden" name="deliveryPricePerKilometerCents" value={eurosToCents(formik.values.deliveryPricePerKilometer)} />
                <input type="hidden" name="deliveryPricingBaseCents" value={eurosToCents(formik.values.deliveryPricingBase)} />
                <input type="hidden" name="deliveryPricingRoundingCents" value={eurosToCents(formik.values.deliveryPricingRounding)} />
                <input type="hidden" name="deliveryPricingMinimumCents" value={eurosToCents(formik.values.deliveryPricingMinimum)} />
                <input type="hidden" name="deliverySetupSupplementCents" value={eurosToCents(formik.values.deliverySetupSupplement)} />
                <input type="hidden" name="useTravelTimeForDeliveryCalendarEvents" value={formik.values.useTravelTimeForDeliveryCalendarEvents ? "true" : "false"} />
                <input type="hidden" name="deliveryDefaultOriginKey" value={formik.values.deliveryDefaultOriginKey} />
                <input type="hidden" name="fuelSurchargeEnabled" value={formik.values.fuelSurchargeEnabled ? "true" : "false"} />
                <input type="hidden" name="fuelType" value={formik.values.fuelType} />
                <input type="hidden" name="fuelConsumptionLitersPer100Km" value={Number(formik.values.fuelConsumption || 0)} />
                <input type="hidden" name="fuelReferencePriceCents" value={eurosToCents(formik.values.fuelReferencePrice)} />
                <input type="hidden" name="enableAdvancePaymentBankTransferInstruction" value={formik.values.enableAdvancePaymentBankTransferInstruction ? "true" : "false"} />
                <input type="hidden" name="advancePaymentBankTransferThresholdDays" value={formik.values.advancePaymentBankTransferThresholdDays} />
                <input type="hidden" name="advancePaymentBankTransferBeneficiary" value={formik.values.advancePaymentBankTransferBeneficiary} />
                <input type="hidden" name="advancePaymentBankTransferIban" value={formik.values.advancePaymentBankTransferIban} />
                <input type="hidden" name="advancePaymentBankTransferBic" value={formik.values.advancePaymentBankTransferBic} />
                <input type="hidden" name="advancePaymentBankTransferBankName" value={formik.values.advancePaymentBankTransferBankName} />
                <input type="hidden" name="advancePaymentBankTransferReferenceTemplate" value={formik.values.advancePaymentBankTransferReferenceTemplate} />
                <input type="hidden" name="advancePaymentBankTransferInstructionTemplate" value={formik.values.advancePaymentBankTransferInstructionTemplate} />
                <input type="hidden" name="enableUpcomingRentalPushNotification" value={formik.values.enableUpcomingRentalPushNotification ? "true" : "false"} />
                <input type="hidden" name="upcomingRentalNotificationLeadTimeHours" value={formik.values.upcomingRentalNotificationLeadTimeHours} />
                <input type="hidden" name="pushoverUser" value={formik.values.pushoverUser} />
                <input type="hidden" name="pushoverToken" value={formik.values.pushoverToken} />
                <input type="hidden" name="pushoverSound" value={formik.values.pushoverSound} />
                <input type="hidden" name="merchandisePurchaseReminderHorizonDays" value={formik.values.merchandisePurchaseReminderHorizonDays} />
                <input type="hidden" name="defaultMerchandiseUnit" value={formik.values.defaultMerchandiseUnit} />
                <input type="hidden" name="defaultMerchandiseReorderThreshold" value={formik.values.defaultMerchandiseReorderThreshold} />
                <input type="hidden" name="autoDeductMerchandiseStockOnRental" value={formik.values.autoDeductMerchandiseStockOnRental ? "true" : "false"} />
                <input type="hidden" name="autoRestoreMerchandiseStockOnCancellation" value={formik.values.autoRestoreMerchandiseStockOnCancellation ? "true" : "false"} />
                <input type="hidden" name="clientEmailTemplatesJson" value={JSON.stringify(formik.values.clientEmailTemplates)} />
                <input type="hidden" name="clientEmailConditionalTextsJson" value={JSON.stringify(formik.values.clientEmailConditionalTexts)} />

                <div className="account-submit-bar">
                    <span>Les paramètres seront utilisés pour les prochaines vérifications et locations.</span>
                    <Button type="submit" icon="pi pi-save" label={isSaving ? "Enregistrement..." : "Enregistrer"} loading={isSaving} disabled={isSaving} />
                </div>
            </Form>
            <Dialog header="Configurer Google Calendar" visible={googleCalendarHelpVisible} className="settings-google-help-dialog" modal style={{ width: "min(760px, 95vw)" }} onHide={() => setGoogleCalendarHelpVisible(false)}>
                <div className="settings-google-help">
                    <p>Pour synchroniser Lokapp avec Google Calendar, créez un service account Google Cloud puis partagez l'agenda avec lui.</p>
                    <ol>
                        <li>Ouvrez Google Cloud Console, créez ou sélectionnez un projet, puis activez l'API Google Calendar.</li>
                        <li>Dans IAM et administration, créez un compte de service, par exemple <code>lokapp-calendar-sync</code>.</li>
                        <li>Dans l'onglet Clés du compte de service, ajoutez une clé au format JSON. Copiez tout le contenu du fichier téléchargé dans le champ JSON du service account.</li>
                        <li>Dans Google Calendar, ouvrez Paramètres et partage de l'agenda cible, puis ajoutez l'email du service account avec le droit Modifier les événements.</li>
                        <li>Copiez l'identifiant de l'agenda dans la section Intégrer l'agenda et collez-le dans Lokapp.</li>
                    </ol>
                    <div className="settings-google-help-warning">
                        <i className="pi pi-lock" aria-hidden="true"></i>
                        <span>Le fichier JSON contient une clé privée. Gardez-le secret, comme un mot de passe.</span>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
